/**
 * FULLY MERCHED — T-Shirt Designer
 * tshirt-designer.js
 *
 * Powered by Fabric.js 5.x
 * Handles: canvas drawing, image upload, text, clipart, variant changes, cart submission
 */

(function () {
  'use strict';

  // ================================================================
  // MAIN DESIGNER OBJECT
  // ================================================================
  const IMGBB_KEY = 'b234ca9d9be9e26ba8e43c42e2b6ec64';

  const TshirtDesigner = {

    // ---- State ----
    canvas: null,
    currentView: 'front',
    currentLocation: null,  // no print location selected until the customer chooses one
    currentPosition: '',   // raw Print-Position variant value for the active location
    _currentColour: '',    // currently selected colour swatch name
    zoomLevel: 1,
    designs: { front: null, back: null }, // stored as fabric JSON
    uploadedImages: [],   // { dataUrl, thumbEl }
    // Original (un-flattened) artwork uploads, keyed by canvas dataUrl.
    // The composite preview is a 1200px JPEG of the whole garment — useless for
    // production print — so each source file is also uploaded at full resolution
    // and its URL attached to the order as an "Artwork ..." line-item property.
    _artwork: new Map(),  // dataUrl -> { promise, url }
    resizeTimer: null,
    _tplGen: 0,
    shirtHex: '#cccccc',       // current shirt fill colour
    _userPickedTextColour: false, // true once the user manually chooses a font colour
    _textCommitted: false,     // true once the text in the box has been added to canvas
    mockupNaturalW: 800,       // fallback dimensions when SVG injection fails
    mockupNaturalH: 800,

    // Undo / Redo history
    _historyStack:   [],
    _historyIndex:   -1,
    _historyPaused:  false,
    _historyTimer:   null,
    // localStorage auto-save
    _storageTimer:   null,

    // Current clipart category
    _clipartCat: 'basic',

    // Active print area (updated by location + view)
    PRINT_AREA: {
      topPct:    0.19,
      leftPct:   0.28,
      widthPct:  0.44,
      heightPct: 0.52
    },

    // Default print area for headwear (caps / beanies / bucket hats).
    // Starts centred on the front panel; the user can then drag the whole
    // area (via the "Move area" handle) to position it exactly where the
    // print needs to go. Horizontally centred: leftPct = (1 - widthPct) / 2.
    HEADWEAR_AREA: {
      topPct:    0.38,
      leftPct:   0.33,   // (1 - 0.34) / 2
      widthPct:  0.34,
      heightPct: 0.22
    },

    // Print location definitions — sides and per-view print area percentages
    PRINT_LOCATIONS: {
      'front-only': {
        label: 'Front Only',
        sides: ['front'],
        areas: {
          front: { topPct: 0.19, leftPct: 0.28, widthPct: 0.44, heightPct: 0.52 },
        },
      },
      'back-only': {
        label: 'Back Only',
        sides: ['back'],
        areas: {
          back: { topPct: 0.19, leftPct: 0.28, widthPct: 0.44, heightPct: 0.52 },
        },
      },
      'pocket-only': {
        label: 'Pocket Only',
        sides: ['front'],
        areas: {
          front: { topPct: 0.18, leftPct: 0.52, widthPct: 0.18, heightPct: 0.20 },
        },
      },
      'front-back': {
        label: 'Front + Back',
        sides: ['front', 'back'],
        areas: {
          front: { topPct: 0.19, leftPct: 0.28, widthPct: 0.44, heightPct: 0.52 },
          back:  { topPct: 0.19, leftPct: 0.28, widthPct: 0.44, heightPct: 0.52 },
        },
      },
      'pocket-back': {
        label: 'Pocket + Back',
        sides: ['front', 'back'],
        areas: {
          front: { topPct: 0.18, leftPct: 0.52, widthPct: 0.18, heightPct: 0.20 },
          back:  { topPct: 0.19, leftPct: 0.28, widthPct: 0.44, heightPct: 0.52 },
        },
      },

      // Hoodie sleeve combos
      'front-sleeve': {
        label: 'Front + Sleeve',
        sides: ['front'],
        areas: {
          front: { topPct: 0.19, leftPct: 0.28, widthPct: 0.44, heightPct: 0.52 },
        },
      },
      'front-back-sleeve': {
        label: 'Front + Back + Sleeve',
        sides: ['front', 'back'],
        areas: {
          front: { topPct: 0.19, leftPct: 0.28, widthPct: 0.44, heightPct: 0.52 },
          back:  { topPct: 0.19, leftPct: 0.28, widthPct: 0.44, heightPct: 0.52 },
        },
      },

      // Polo shirt pocket positions
      'polo-pocket-left': {
        label: 'Front Pocket Left',
        sides: ['front'],
        areas: {
          front: { topPct: 0.18, leftPct: 0.52, widthPct: 0.18, heightPct: 0.20 },
        },
      },
      'polo-pocket-both': {
        label: 'Front Pocket Left & Right',
        sides: ['front'],
        areas: {
          front: { topPct: 0.18, leftPct: 0.28, widthPct: 0.44, heightPct: 0.20 },
        },
      },
      'polo-pocket-left-back': {
        label: 'Front Pocket Left & Back',
        sides: ['front', 'back'],
        areas: {
          front: { topPct: 0.18, leftPct: 0.52, widthPct: 0.18, heightPct: 0.20 },
          back:  { topPct: 0.19, leftPct: 0.28, widthPct: 0.44, heightPct: 0.52 },
        },
      },
      'polo-pocket-both-back': {
        label: 'Front Pocket Left & Right + Back',
        sides: ['front', 'back'],
        areas: {
          front: { topPct: 0.18, leftPct: 0.28, widthPct: 0.44, heightPct: 0.20 },
          back:  { topPct: 0.19, leftPct: 0.28, widthPct: 0.44, heightPct: 0.52 },
        },
      },
    },

    // Colour name → hex map for SVG shirt fill
    COLOUR_MAP: {
      'black': '#1a1a1a',
      'white': '#f8f8f8',
      'navy': '#1f3a6e',
      'navy blue': '#1f3a6e',
      'red': '#d32f2f',
      'royal blue': '#1565c0',
      'blue': '#1565c0',
      'grey': '#9e9e9e',
      'gray': '#9e9e9e',
      'light grey': '#e0e0e0',
      'light gray': '#e0e0e0',
      'heather grey': '#b8b8b8',
      'heather gray': '#b8b8b8',
      'charcoal': '#4a4a4a',
      'dark grey': '#616161',
      'dark gray': '#616161',
      'forest green': '#2e7d32',
      'green': '#388e3c',
      'bottle green': '#1b5e20',
      'dark green': '#1b5e20',
      'yellow': '#f9a825',
      'gold': '#f9a825',
      'orange': '#e65100',
      'pink': '#f06292',
      'hot pink': '#e91e63',
      'fuchsia': '#e91e63',
      'purple': '#6a1b9a',
      'violet': '#6a1b9a',
      'maroon': '#880e4f',
      'burgundy': '#7b1b2e',
      'wine': '#7b1b2e',
      'teal': '#00695c',
      'sky blue': '#039be5',
      'light blue': '#4fc3f7',
      'baby blue': '#90caf9',
      'brown': '#6d4c41',
      'cream': '#f5f0e1',
      'natural': '#f5f0e1',
      'ecru': '#f5f0e1',
      'sand': '#d4b896',
      'khaki': '#c8b560',
      'olive': '#827717',
      'coral': '#e57373',
      'mint': '#80cbc4',
      'lavender': '#9575cd',
      'lilac': '#ce93d8',
      'slate': '#546e7a',
      'ash grey': '#b0b0b0',
      'ash gray': '#b0b0b0',
      'stone': '#bdbdbd',
      'caramel': '#a1672a',
      'indigo': '#283593',
      'turquoise': '#0097a7',
      'mustard': '#f57f17',
      'dusty pink': '#e8a0a0',
      'rose': '#e91e63',
      'cherry red': '#c62828',
    },

    CLIPART: [
      // ── Basic ────────────────────────────────────────────────
      { cat:'basic', label: 'Star',      fill: true,  icon: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>' },
      { cat:'basic', label: 'Heart',     fill: true,  icon: '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' },
      { cat:'basic', label: 'Lightning', fill: true,  icon: '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>' },
      { cat:'basic', label: 'Flame',     fill: false, icon: '<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>' },
      { cat:'basic', label: 'Crown',     fill: false, icon: '<path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z"/><line x1="5" y1="16" x2="19" y2="16"/>' },
      { cat:'basic', label: 'Diamond',   fill: false, icon: '<path d="M6 3h12l4 6-10 13L2 9l4-6z"/><line x1="2" y1="9" x2="22" y2="9"/>' },
      { cat:'basic', label: 'Moon',      fill: false, icon: '<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>' },
      { cat:'basic', label: 'Sun',       fill: false, icon: '<circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>' },
      { cat:'basic', label: 'Target',    fill: false, icon: '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>' },
      { cat:'basic', label: 'Smile',     fill: false, icon: '<circle cx="12" cy="12" r="10"/><path d="M8 13s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>' },
      { cat:'basic', label: 'Shield',    fill: false, icon: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>' },
      { cat:'basic', label: 'Globe',     fill: false, icon: '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>' },
      { cat:'basic', label: 'Skull',     fill: false, icon: '<path d="M12 2C8 2 4 6 4 10c0 3 1.5 5 3 6v2h10v-2c1.5-1 3-3 3-6 0-4-4-8-8-8z"/><line x1="9" y1="21" x2="9" y2="18"/><line x1="15" y1="21" x2="15" y2="18"/><line x1="9" y1="12" x2="9" y2="12"/><line x1="15" y1="12" x2="15" y2="12"/>' },
      // ── Sports ───────────────────────────────────────────────
      { cat:'sports', label: 'Trophy',   fill: false, icon: '<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/>' },
      { cat:'sports', label: 'Medal',    fill: false, icon: '<circle cx="12" cy="17" r="5"/><path d="M8.56 2.9A7 7 0 0 1 19 9v3h2"/><path d="M3 9h2v3a7 7 0 0 0 4.56 6.5"/><line x1="7" y1="2" x2="7" y2="12"/><line x1="17" y1="2" x2="17" y2="12"/>' },
      { cat:'sports', label: 'Football', fill: false, icon: '<circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 0 20"/><path d="M2 12h20"/><path d="M12 2c2.5 2.5 4 5.5 4 10s-1.5 7.5-4 10"/><path d="M12 2C9.5 4.5 8 7.5 8 12s1.5 7.5 4 10"/>' },
      { cat:'sports', label: 'Weights',  fill: false, icon: '<line x1="6" y1="12" x2="18" y2="12"/><rect x="1" y="9" width="4" height="6" rx="1"/><rect x="19" y="9" width="4" height="6" rx="1"/><rect x="5" y="10" width="2" height="4" rx="1"/><rect x="17" y="10" width="2" height="4" rx="1"/>' },
      { cat:'sports', label: 'Running',  fill: false, icon: '<circle cx="13" cy="4" r="2"/><path d="m9 20 1-5 2 2 4-5"/><path d="m7 12 2-3 3 1 3-4"/>' },
      { cat:'sports', label: 'Bicycle',  fill: false, icon: '<circle cx="5" cy="17" r="3"/><circle cx="19" cy="17" r="3"/><path d="M12 17 8 5"/><line x1="6" y1="8" x2="12" y2="8"/><path d="m12 8 4 9"/><path d="m8 5 3 3"/>' },
      { cat:'sports', label: 'Flag',     fill: false, icon: '<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>' },
      { cat:'sports', label: 'Whistle',  fill: false, icon: '<path d="M6 12 2 8l4-4 4 4-1 1"/><path d="M14.5 7.5 8 14l-1 6 6-1 6.5-6.5a4.95 4.95 0 0 0-5-5z"/><circle cx="17" cy="10" r="1"/>' },
      { cat:'sports', label: 'Stopwatch',fill: false, icon: '<circle cx="12" cy="14" r="8"/><path d="M12 6V2"/><path d="M8 2h8"/><path d="m4.9 7.1 1.4 1.5"/><path d="M12 10v4l2 2"/>' },
      // ── Nature ───────────────────────────────────────────────
      { cat:'nature', label: 'Tree',     fill: false, icon: '<path d="M17 22V12.5a10 10 0 1 0-10 0V22"/><line x1="12" y1="22" x2="12" y2="12"/>' },
      { cat:'nature', label: 'Leaf',     fill: false, icon: '<path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>' },
      { cat:'nature', label: 'Mountain', fill: false, icon: '<polygon points="3 20 9 5 15 20"/><polygon points="9 20 15 8 21 20"/>' },
      { cat:'nature', label: 'Wave',     fill: false, icon: '<path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>' },
      { cat:'nature', label: 'Cloud',    fill: false, icon: '<path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>' },
      { cat:'nature', label: 'Flower',   fill: false, icon: '<circle cx="12" cy="12" r="3"/><path d="M12 1v3M12 20v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M1 12h3M20 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/>' },
      { cat:'nature', label: 'Snowflake',fill: false, icon: '<line x1="12" y1="2" x2="12" y2="22"/><path d="M17 7l-5 5-5-5"/><path d="M17 17l-5-5-5 5"/><path d="M2 12h20"/><path d="M7 7l-5 5 5 5"/><path d="M17 7l5 5-5 5"/>' },
      { cat:'nature', label: 'Rain',     fill: false, icon: '<line x1="16" y1="13" x2="16" y2="21"/><line x1="8" y1="13" x2="8" y2="21"/><line x1="12" y1="15" x2="12" y2="23"/><path d="M20 16.58A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25"/>' },
      // ── Business ─────────────────────────────────────────────
      { cat:'business', label: 'Rocket',    fill: false, icon: '<path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>' },
      { cat:'business', label: 'Chart',     fill: false, icon: '<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>' },
      { cat:'business', label: 'Dollar',    fill: false, icon: '<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>' },
      { cat:'business', label: 'Briefcase', fill: false, icon: '<rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>' },
      { cat:'business', label: 'Music',     fill: false, icon: '<path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>' },
      { cat:'business', label: 'Camera',    fill: false, icon: '<path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>' },
      { cat:'business', label: 'Mic',       fill: false, icon: '<path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>' },
    ],

    TEMPLATES: [
      // ── Text-only templates ───────────────────────────────────────
      {
        id: 'bold-statement',
        label: 'Bold Statement',
        preview: `<svg viewBox="0 0 90 80" xmlns="http://www.w3.org/2000/svg"><text x="45" y="52" text-anchor="middle" font-family="Impact" font-size="30" fill="#191946">TEXT!</text></svg>`,
        apply(canvas, W, H, isDark) {
          canvas.clear();
          const fg = isDark ? '#ffffff' : '#191946';
          const fs = Math.min(40, Math.max(14, Math.round(Math.min(W,H)*0.13)));
          canvas.add(new fabric.IText('YOUR TEXT!', { left:W/2, top:H/2, originX:'center', originY:'center', fontFamily:'Impact', fontSize:fs, fill:fg, textAlign:'center' }));
          canvas.renderAll();
        },
      },
      {
        id: 'stacked-lines',
        label: 'Stacked Lines',
        preview: `<svg viewBox="0 0 90 80" xmlns="http://www.w3.org/2000/svg"><text x="45" y="20" text-anchor="middle" font-family="Georgia" font-size="10" font-style="italic" fill="#32c5ee">Me &amp; You</text><text x="45" y="45" text-anchor="middle" font-family="Arial" font-size="17" font-weight="bold" fill="#191946">YOUR TEXT</text><text x="45" y="64" text-anchor="middle" font-family="Georgia" font-size="11" fill="#32c5ee">Here</text></svg>`,
        apply(canvas, W, H, isDark) {
          canvas.clear();
          const fg  = isDark ? '#ffffff' : '#191946';
          const sub = '#32c5ee';
          const sm = Math.min(16, Math.max(10, Math.round(Math.min(W,H)*0.065)));
          const lg = Math.min(28, Math.max(14, Math.round(Math.min(W,H)*0.11)));
          canvas.add(new fabric.IText('Me & You',  { left:W/2, top:H*0.22, originX:'center', originY:'center', fontFamily:'Georgia', fontSize:sm, fill:sub, textAlign:'center', fontStyle:'italic' }));
          canvas.add(new fabric.IText('YOUR TEXT', { left:W/2, top:H*0.50, originX:'center', originY:'center', fontFamily:'Arial',   fontSize:lg, fill:fg,  textAlign:'center', fontWeight:'bold' }));
          canvas.add(new fabric.IText('Here',      { left:W/2, top:H*0.76, originX:'center', originY:'center', fontFamily:'Georgia', fontSize:sm, fill:sub, textAlign:'center' }));
          canvas.renderAll();
        },
      },
      {
        id: 'angled-text',
        label: 'Up Angle',
        preview: `<svg viewBox="0 0 90 80" xmlns="http://www.w3.org/2000/svg"><text x="45" y="52" text-anchor="middle" font-family="Impact" font-size="28" fill="#191946" transform="rotate(-15,45,45)">TEXT!</text></svg>`,
        apply(canvas, W, H, isDark) {
          canvas.clear();
          const fg = isDark ? '#ffffff' : '#191946';
          const fs = Math.min(36, Math.max(14, Math.round(Math.min(W,H)*0.12)));
          canvas.add(new fabric.IText('YOUR TEXT!', { left:W/2, top:H/2, originX:'center', originY:'center', fontFamily:'Impact', fontSize:fs, fill:fg, textAlign:'center', angle:-15 }));
          canvas.renderAll();
        },
      },
      {
        id: 'two-colour',
        label: 'Two Colour',
        preview: `<svg viewBox="0 0 90 80" xmlns="http://www.w3.org/2000/svg"><text x="45" y="30" text-anchor="middle" font-family="Impact" font-size="18" fill="#32c5ee">YOUR TEXT</text><text x="45" y="60" text-anchor="middle" font-family="Impact" font-size="24" fill="#191946">HERE</text></svg>`,
        apply(canvas, W, H, isDark) {
          canvas.clear();
          const fg = isDark ? '#ffffff' : '#191946';
          const f1 = Math.min(24, Math.max(12, Math.round(Math.min(W,H)*0.10)));
          const f2 = Math.min(32, Math.max(16, Math.round(Math.min(W,H)*0.13)));
          canvas.add(new fabric.IText('YOUR TEXT', { left:W/2, top:H*0.33, originX:'center', originY:'center', fontFamily:'Impact', fontSize:f1, fill:'#32c5ee', textAlign:'center' }));
          canvas.add(new fabric.IText('HERE',      { left:W/2, top:H*0.63, originX:'center', originY:'center', fontFamily:'Impact', fontSize:f2, fill:fg,       textAlign:'center' }));
          canvas.renderAll();
        },
      },
      {
        id: 'cursive-quote',
        label: 'Cursive Quote',
        preview: `<svg viewBox="0 0 90 80" xmlns="http://www.w3.org/2000/svg"><text x="45" y="28" text-anchor="middle" font-family="Georgia" font-size="9" fill="#32c5ee">"</text><text x="45" y="46" text-anchor="middle" font-family="Georgia" font-size="13" font-style="italic" fill="#191946">Your Quote</text><text x="45" y="62" text-anchor="middle" font-family="Arial" font-size="8" fill="#32c5ee">— Your Name</text></svg>`,
        apply(canvas, W, H, isDark) {
          canvas.clear();
          const fg  = isDark ? '#ffffff' : '#191946';
          const sub = '#32c5ee';
          const q   = Math.min(36, Math.max(16, Math.round(Math.min(W,H)*0.14)));
          const lg  = Math.min(22, Math.max(12, Math.round(Math.min(W,H)*0.09)));
          const sm  = Math.min(14, Math.max(9,  Math.round(Math.min(W,H)*0.06)));
          canvas.add(new fabric.IText('"',          { left:W/2, top:H*0.12, originX:'center', originY:'center', fontFamily:'Georgia', fontSize:q,  fill:sub, textAlign:'center' }));
          canvas.add(new fabric.IText('Your Quote', { left:W/2, top:H*0.50, originX:'center', originY:'center', fontFamily:'Georgia', fontSize:lg, fill:fg,  textAlign:'center', fontStyle:'italic' }));
          canvas.add(new fabric.IText('— Your Name',{ left:W/2, top:H*0.78, originX:'center', originY:'center', fontFamily:'Arial',   fontSize:sm, fill:sub, textAlign:'center' }));
          canvas.renderAll();
        },
      },
      // ── Company / workwear logo templates (dummy placeholders) ────
      {
        id: 'company-logo-name',
        label: 'Logo + Name',
        needsImage: true,
        imageKind: 'logo',
        preview: `<svg viewBox="0 0 90 80" xmlns="http://www.w3.org/2000/svg"><rect x="33" y="8" width="24" height="24" rx="4" fill="none" stroke="#191946" stroke-width="2"/><text x="45" y="24" text-anchor="middle" font-family="Arial" font-size="7" fill="#191946">LOGO</text><text x="45" y="50" text-anchor="middle" font-family="Arial" font-size="12" font-weight="bold" fill="#191946">COMPANY</text><text x="45" y="64" text-anchor="middle" font-family="Arial" font-size="12" font-weight="bold" fill="#191946">NAME</text></svg>`,
        apply(canvas, W, H, isDark) {
          canvas.clear();
          const fg  = isDark ? '#ffffff' : '#191946';
          const box = Math.round(Math.min(W, H) * 0.22);
          const cx  = W / 2, cy = H * 0.30;
          const rect = new fabric.Rect({ left: cx, top: cy, originX: 'center', originY: 'center', width: box, height: box, rx: box * 0.16, ry: box * 0.16, fill: 'transparent', stroke: fg, strokeWidth: Math.max(2, box * 0.05) });
          const logo = new fabric.IText('LOGO', { left: cx, top: cy, originX: 'center', originY: 'center', fontFamily: 'Arial', fontSize: Math.max(9, box * 0.22), fill: fg, textAlign: 'center' });
          const name = new fabric.IText('COMPANY NAME', { left: cx, top: H * 0.64, originX: 'center', originY: 'center', fontFamily: 'Arial', fontSize: Math.min(26, Math.max(12, Math.round(Math.min(W, H) * 0.09))), fill: fg, fontWeight: 'bold', textAlign: 'center' });
          rect._tsdRole = 'photo-frame'; rect._tsdNoClip = true; logo._tsdRole = 'photo-hint';
          canvas.add(rect, logo, name);
          canvas.renderAll();
        },
      },
      {
        id: 'company-logo-tagline',
        label: 'Logo + Tagline',
        needsImage: true,
        imageKind: 'logo',
        preview: `<svg viewBox="0 0 90 80" xmlns="http://www.w3.org/2000/svg"><circle cx="45" cy="20" r="13" fill="none" stroke="#191946" stroke-width="2"/><text x="45" y="23" text-anchor="middle" font-family="Arial" font-size="7" fill="#191946">LOGO</text><text x="45" y="50" text-anchor="middle" font-family="Arial" font-size="12" font-weight="bold" fill="#191946">COMPANY</text><text x="45" y="63" text-anchor="middle" font-family="Georgia" font-size="8" font-style="italic" fill="#32c5ee">your tagline here</text></svg>`,
        apply(canvas, W, H, isDark) {
          canvas.clear();
          const fg  = isDark ? '#ffffff' : '#191946';
          const sub = '#32c5ee';
          const r   = Math.round(Math.min(W, H) * 0.11);
          const cx  = W / 2, cy = H * 0.26;
          const circ = new fabric.Circle({ left: cx, top: cy, originX: 'center', originY: 'center', radius: r, fill: 'transparent', stroke: fg, strokeWidth: Math.max(2, r * 0.1) });
          const logo = new fabric.IText('LOGO', { left: cx, top: cy, originX: 'center', originY: 'center', fontFamily: 'Arial', fontSize: Math.max(9, r * 0.42), fill: fg, textAlign: 'center' });
          const name = new fabric.IText('COMPANY NAME', { left: cx, top: H * 0.60, originX: 'center', originY: 'center', fontFamily: 'Arial', fontSize: Math.min(24, Math.max(12, Math.round(Math.min(W, H) * 0.085))), fill: fg, fontWeight: 'bold', textAlign: 'center' });
          const tag  = new fabric.IText('your tagline here', { left: cx, top: H * 0.76, originX: 'center', originY: 'center', fontFamily: 'Georgia', fontSize: Math.min(15, Math.max(9, Math.round(Math.min(W, H) * 0.05))), fill: sub, fontStyle: 'italic', textAlign: 'center' });
          circ._tsdRole = 'photo-frame'; circ._tsdNoClip = true; logo._tsdRole = 'photo-hint';
          canvas.add(circ, logo, name, tag);
          canvas.renderAll();
        },
      },
      {
        id: 'company-name-only',
        label: 'Name Only',
        preview: `<svg viewBox="0 0 90 80" xmlns="http://www.w3.org/2000/svg"><text x="45" y="38" text-anchor="middle" font-family="Arial" font-size="14" font-weight="bold" fill="#191946">COMPANY</text><rect x="22" y="44" width="46" height="2" fill="#191946"/><text x="45" y="60" text-anchor="middle" font-family="Arial" font-size="9" letter-spacing="2" fill="#191946">NAME</text></svg>`,
        apply(canvas, W, H, isDark) {
          canvas.clear();
          const fg = isDark ? '#ffffff' : '#191946';
          const cx = W / 2;
          const lg = Math.min(34, Math.max(16, Math.round(Math.min(W, H) * 0.12)));
          const sm = Math.min(18, Math.max(10, Math.round(Math.min(W, H) * 0.06)));
          const name = new fabric.IText('COMPANY', { left: cx, top: H * 0.42, originX: 'center', originY: 'center', fontFamily: 'Arial', fontSize: lg, fill: fg, fontWeight: 'bold', textAlign: 'center' });
          const rule = new fabric.Rect({ left: cx, top: H * 0.56, originX: 'center', originY: 'center', width: Math.round(W * 0.42), height: Math.max(2, Math.round(H * 0.008)), fill: fg });
          const sub  = new fabric.IText('NAME', { left: cx, top: H * 0.66, originX: 'center', originY: 'center', fontFamily: 'Arial', fontSize: sm, fill: fg, charSpacing: 300, textAlign: 'center' });
          canvas.add(name, rule, sub);
          canvas.renderAll();
        },
      },
      // ── Image (photo) + text templates ────────────────────────────
      // The dashed box is a "drop your photo here" guide — the customer
      // uploads an image (Upload tab) and positions it inside, then deletes
      // the guide box + hint. Same placeholder pattern as the logo templates.
      {
        id: 'photo-caption',
        label: 'Photo + Caption',
        needsImage: true,
        preview: `<svg viewBox="0 0 90 80" xmlns="http://www.w3.org/2000/svg"><rect x="24" y="6" width="42" height="42" rx="4" fill="none" stroke="#191946" stroke-width="2" stroke-dasharray="5 3"/><circle cx="37" cy="20" r="4" fill="#32c5ee"/><path d="M28 44 l9-11 6 7 6-6 9 10z" fill="#32c5ee"/><text x="45" y="66" text-anchor="middle" font-family="Impact" font-size="16" fill="#191946">YOUR TEXT</text></svg>`,
        apply(canvas, W, H, isDark) {
          canvas.clear();
          const fg  = isDark ? '#ffffff' : '#191946';
          const sub = '#32c5ee';
          const cx  = W / 2;
          const box = new fabric.Rect({ left:cx, top:H*0.36, originX:'center', originY:'center', width:W*0.58, height:H*0.50, rx:10, ry:10, fill:'transparent', stroke:fg, strokeWidth:2, strokeDashArray:[9,5] });
          const hint= new fabric.IText('ADD PHOTO', { left:cx, top:H*0.36, originX:'center', originY:'center', fontFamily:'Arial', fontSize:Math.max(9, Math.round(Math.min(W,H)*0.045)), fill:sub, textAlign:'center', charSpacing:150 });
          const cap = new fabric.IText('YOUR TEXT', { left:cx, top:H*0.78, originX:'center', originY:'center', fontFamily:'Impact', fontSize:Math.min(32, Math.max(14, Math.round(Math.min(W,H)*0.11))), fill:fg, textAlign:'center' });
          box._tsdRole = 'photo-frame'; hint._tsdRole = 'photo-hint';
          canvas.add(box, hint, cap);
          canvas.renderAll();
        },
      },
      {
        id: 'headline-photo',
        label: 'Headline + Photo',
        needsImage: true,
        preview: `<svg viewBox="0 0 90 80" xmlns="http://www.w3.org/2000/svg"><text x="45" y="18" text-anchor="middle" font-family="Impact" font-size="15" fill="#191946">YOUR TEXT</text><rect x="24" y="26" width="42" height="46" rx="4" fill="none" stroke="#191946" stroke-width="2" stroke-dasharray="5 3"/><circle cx="37" cy="42" r="4" fill="#32c5ee"/><path d="M28 68 l9-11 6 7 6-6 9 10z" fill="#32c5ee"/></svg>`,
        apply(canvas, W, H, isDark) {
          canvas.clear();
          const fg  = isDark ? '#ffffff' : '#191946';
          const sub = '#32c5ee';
          const cx  = W / 2;
          const head= new fabric.IText('YOUR TEXT', { left:cx, top:H*0.14, originX:'center', originY:'center', fontFamily:'Impact', fontSize:Math.min(32, Math.max(14, Math.round(Math.min(W,H)*0.11))), fill:fg, textAlign:'center' });
          const box = new fabric.Rect({ left:cx, top:H*0.60, originX:'center', originY:'center', width:W*0.58, height:H*0.56, rx:10, ry:10, fill:'transparent', stroke:fg, strokeWidth:2, strokeDashArray:[9,5] });
          const hint= new fabric.IText('ADD PHOTO', { left:cx, top:H*0.60, originX:'center', originY:'center', fontFamily:'Arial', fontSize:Math.max(9, Math.round(Math.min(W,H)*0.045)), fill:sub, textAlign:'center', charSpacing:150 });
          box._tsdRole = 'photo-frame'; hint._tsdRole = 'photo-hint';
          canvas.add(head, box, hint);
          canvas.renderAll();
        },
      },
    ],

    // ---- Init ----
    init() {
      // Resolve shirt dark/light state synchronously so templates applied before
      // the async SVG fetch completes already use the correct text colour (Bug 3)
      const _ic = document.getElementById('tsd-mockup-container')?.dataset.initColour || '';
      if (_ic) {
        this.shirtHex = this.resolveColourHex(_ic);
        document.getElementById('tsd-canvas')
          ?.closest('section')
          ?.setAttribute('data-shirt-dark', this.isColourDark(_ic) ? '1' : '0');
      }

      this.setupCanvas();
      this.bindViewToggle();
      this.bindFileUpload();
      this.bindTextControls();
      this.bindObjectControls();
      this.bindTabs();
      this.buildClipart();
      this.buildTemplates();
      this.bindLocationPicker();
      this.syncViewToggleVisibility();
      this.bindColourSwatches();
      this.bindSizeQtyTable();
      this.bindFormSubmit();
      this.bindBuyNow();
      this.bindAccordions();
      this.bindZoom();
      this.bindKeyboard();
      this.bindMobileSheet();
      this.relocateColourGroup();
      this.bindUndoRedo();
      this.bindClipartCategories();
      this.loadGoogleFonts();
      this.buildPremiumFonts();
      this.initLocalStorage();
      this._pushHistory(); // baseline snapshot

      // Window resize fallback for browsers without ResizeObserver
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          this.resizeCanvas();
          this.relocateColourGroup();
        }, 150);
      });
    },

    // ================================================================
    // CANVAS SETUP
    // ================================================================
    setupCanvas() {
      this.canvas = new fabric.Canvas('tsd-canvas', {
        preserveObjectStacking: true,
        selection: true,
        defaultCursor: 'default',
        hoverCursor: 'move',
      });

      this.canvas.on('selection:created',  () => this.onSelectionChange());
      this.canvas.on('selection:updated',  () => this.onSelectionChange());
      this.canvas.on('selection:cleared',  () => this.onSelectionChange());
      this.canvas.on('object:modified',    () => { this.constrainToBounds(true); this.updateInspector(); this._debouncePushHistory(); });
      this.canvas.on('object:moving',      () => this.constrainToBounds(false));
      this.canvas.on('object:scaling',     () => this.constrainToBounds(true));
      this.canvas.on('object:rotating',    () => this.constrainToBounds(false));
      this.canvas.on('object:added',       () => { this.updatePrintAreaState(); this.updateBlendModeForObjects(); this._debouncePushHistory(); });
      this.canvas.on('object:removed',     () => { this.updatePrintAreaState(); this.updateBlendModeForObjects(); this._debouncePushHistory(); });

      // Initialise print area from the default location (front-only)
      this.updatePrintAreaForCurrentView();

      // Caps/beanies have no single obvious print spot, so the area stays
      // movable there. On garments it is fixed to the per-location constants.
      if (this.isHeadwear()) this.enableAreaDrag();

      const container = document.getElementById('tsd-mockup-container');

      // Fetch and inject product SVGs inline so we can recolour the shirt body
      const frontUrl = container?.dataset.frontUrl || '';
      const backUrl  = container?.dataset.backUrl  || '';
      const initColour = container?.dataset.initColour || '';

      this.fetchAndInjectSVG(frontUrl, 'tsd-shirt-front').then(injected => {
        if (injected) {
          // Hide the <img> fallback — we have the real inline SVG now
          const img = document.getElementById('tsd-mockup-img');
          if (img) img.style.display = 'none';
          if (initColour) this.applyShirtColour(initColour);
        } else {
          // SVG fetch failed — fall back to <img>, seed natural dimensions
          const img = document.getElementById('tsd-mockup-img');
          if (img) {
            this.mockupNaturalW = img.naturalWidth  || 800;
            this.mockupNaturalH = img.naturalHeight || 800;
          }
          if (initColour) this.updateDesignColoursForShirt(initColour);
        }
        requestAnimationFrame(() => this.resizeCanvas());
      });

      // Cache the back SVG in the background
      if (backUrl) {
        this.fetchAndInjectSVG(backUrl, 'tsd-shirt-back', true).then(() => {});
      }

      // Defer initial resize until after first paint so container has real dimensions
      requestAnimationFrame(() => requestAnimationFrame(() => this.resizeCanvas()));

      // Watch for container size changes (viewport resize, sidebar open/close, etc.)
      if (container && typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(() => {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(() => this.resizeCanvas(), 80);
        }).observe(container);
      }
    },

    resizeCanvas() {
      const container = document.getElementById('tsd-mockup-container');
      const printArea = document.getElementById('tsd-print-area');
      if (!container || !printArea) return;

      const containerRect = container.getBoundingClientRect();
      const containerW = containerRect.width;
      const containerH = containerRect.height;

      // Guard: if container has no size yet, retry after next paint
      if (containerW === 0 || containerH === 0) {
        requestAnimationFrame(() => this.resizeCanvas());
        return;
      }

      // Determine natural dimensions: use inline SVG viewBox if present, else img, else fallback
      let natW = this.mockupNaturalW, natH = this.mockupNaturalH;
      const inlineSvg = document.querySelector('.tsd-shirt-svg:not([style*="display: none"]):not([style*="display:none"])');
      if (inlineSvg) {
        const vb = inlineSvg.viewBox?.baseVal;
        if (vb && vb.width > 0 && vb.height > 0) {
          natW = vb.width;
          natH = vb.height;
        } else {
          natW = containerW;
          natH = containerH;
        }
      } else {
        const img = document.getElementById('tsd-mockup-img');
        if (img && img.naturalWidth > 0) {
          natW = img.naturalWidth;
          natH = img.naturalHeight;
        }
      }
      const scale = Math.min(containerW / natW, containerH / natH);
      const imgW = natW * scale;
      const imgH = natH * scale;
      const imgOffsetX = (containerW - imgW) / 2;
      const imgOffsetY = (containerH - imgH) / 2;

      // Cache geometry so the headwear drag handler can convert px <-> %.
      this._mockupMetrics = { imgW, imgH, imgOffsetX, imgOffsetY };

      const p = this.PRINT_AREA;
      let printW    = Math.round(imgW * p.widthPct);
      let printH    = Math.round(imgH * p.heightPct);
      let printTop  = Math.round(imgOffsetY + (imgH * p.topPct));
      let printLeft = Math.round(imgOffsetX + (imgW * p.leftPct));

      // Universal safety clamp: never let the print area extend outside the
      // fitted mockup image, whatever the per-location %s or garment scale.
      const areaLeft   = Math.max(printLeft, imgOffsetX);
      const areaTop    = Math.max(printTop,  imgOffsetY);
      const areaRight  = Math.min(printLeft + printW, imgOffsetX + imgW);
      const areaBottom = Math.min(printTop  + printH, imgOffsetY + imgH);
      printLeft = Math.round(areaLeft);
      printTop  = Math.round(areaTop);
      printW    = Math.max(0, Math.round(areaRight  - areaLeft));
      printH    = Math.max(0, Math.round(areaBottom - areaTop));

      // Position the print area div exactly over the shirt's printable zone
      printArea.style.top    = printTop  + 'px';
      printArea.style.left   = printLeft + 'px';
      printArea.style.width  = printW    + 'px';
      printArea.style.height = printH    + 'px';

      // Keep Fabric canvas dimensions in sync; rescale all objects to match
      if (this.canvas.width !== printW || this.canvas.height !== printH) {
        const oldW = this.canvas.width;
        const oldH = this.canvas.height;
        this.canvas.setDimensions({ width: printW, height: printH });

        if (oldW > 0 && oldH > 0 && this.canvas.getObjects().length > 0) {
          // Uniform scale (min ratio) avoids distorting text when aspect ratio changes.
          // Position is mapped proportionally so objects stay at the same relative
          // spot in the new canvas (e.g. centred text stays centred).
          const s = Math.min(printW / oldW, printH / oldH);
          this.canvas.getObjects().forEach(obj => {
            obj.set({
              left:   (obj.left / oldW) * printW,
              top:    (obj.top  / oldH) * printH,
              scaleX: obj.scaleX * s,
              scaleY: obj.scaleY * s,
            });
            obj.setCoords();
          });
        }

        this.canvas.renderAll();
      }
    },

    updatePrintAreaState() {
      const el = document.getElementById('tsd-print-area');
      if (!el) return;
      const hasObjects = this.canvas.getObjects().length > 0;
      el.classList.toggle('tsd-has-objects', hasObjects);
    },

    // The canvas uses mix-blend-mode: multiply so solid text looks "printed"
    // into the fabric. Multiply, however, tints raster images with the shirt
    // (variant) colour. When an uploaded image is present, switch the canvas to
    // normal blend so the image keeps its true colours instead of bleeding.
    updateBlendModeForObjects() {
      const section = document.getElementById('tsd-canvas')?.closest('section');
      if (!section) return;
      // Uploaded SVGs arrive as groups, not images, but need the same treatment.
      const hasImage = this.canvas.getObjects().some(o => o.type === 'image' || o._uploadDataUrl);
      section.setAttribute('data-has-raster', hasImage ? '1' : '0');
    },

    // Keep the active object inside the print area. The Fabric canvas is sized
    // exactly to the print box, so anything past its edge is silently cropped
    // out of the exported print file. When `capScale` is set (scale / rotate),
    // shrink an oversized object back to fit before clamping its position —
    // otherwise the customer would order a design with its edges missing.
    constrainToBounds(capScale) {
      const obj = this.canvas.getActiveObject();
      if (!obj) return;
      const w = this.canvas.width;
      const h = this.canvas.height;
      if (!w || !h) return;

      if (capScale) {
        const box = obj.getBoundingRect(true, true);
        if (box.width > w || box.height > h) {
          // At a fixed angle the bounding box scales linearly with scaleX/scaleY,
          // so one uniform factor lands it exactly inside the area.
          const fit = Math.min(w / box.width, h / box.height);
          obj.set({ scaleX: obj.scaleX * fit, scaleY: obj.scaleY * fit });
          obj.setCoords();
        }
      }

      const bBox = obj.getBoundingRect(true, true);
      let dx = 0, dy = 0;

      if (bBox.left < 0) dx = -bBox.left;
      else if (bBox.left + bBox.width > w) dx = w - (bBox.left + bBox.width);

      if (bBox.top < 0) dy = -bBox.top;
      else if (bBox.top + bBox.height > h) dy = h - (bBox.top + bBox.height);

      if (dx || dy) {
        obj.set({ left: obj.left + dx, top: obj.top + dy });
        obj.setCoords();
      }
    },

    // ================================================================
    // VIEW TOGGLE (Front / Back)
    // ================================================================
    bindViewToggle() {
      document.querySelectorAll('.tsd-view-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const view = btn.dataset.view;
          if (view === this.currentView) return;

          const loc = this.PRINT_LOCATIONS[this.currentLocation];
          if (loc && !loc.sides.includes(view)) return;

          this.saveCurrentView();
          this.currentView = view;

          document.querySelectorAll('.tsd-view-btn').forEach(b => b.classList.remove('tsd-view-btn--active'));
          btn.classList.add('tsd-view-btn--active');

          this.updatePrintAreaForCurrentView();
          this.swapMockupImage(view);
          this.loadView(view);

          const label = document.querySelector('.tsd-print-area-label');
          if (label) {
            const isDual = loc?.sides.length > 1;
            label.textContent = view === 'front'
              ? (isDual ? 'Front Print Area' : 'Print Area')
              : 'Back Print Area';
          }
        });
      });
    },

    saveCurrentView() {
      const json = this.canvas.toJSON(['id', 'selectable', '_uploadDataUrl', '_premium', '_premiumKey', '_premiumText', '_premiumFontSize', '_premiumEmPx']);
      // Store the canvas size at save-time so loadView can rescale objects
      json._canvasW = this.canvas.width;
      json._canvasH = this.canvas.height;
      this.designs[this.currentView] = json;
    },

    loadView(view) {
      const saved = this.designs[view];
      if (saved && saved.objects && saved.objects.length > 0) {
        this.canvas.loadFromJSON(saved, () => {
          // Rescale all objects if the canvas size has changed since the design was saved.
          // Uniform scale (min ratio) keeps text undistorted when aspect ratio changes;
          // positions are remapped proportionally so objects stay in the same relative
          // spot within the canvas (centred text stays centred, etc.).
          const savedW = saved._canvasW;
          const savedH = saved._canvasH;
          const cx = this.canvas.width;
          const cy = this.canvas.height;
          if (savedW && savedH && (Math.abs(cx / savedW - 1) > 0.01 || Math.abs(cy / savedH - 1) > 0.01)) {
            const s = Math.min(cx / savedW, cy / savedH);
            this.canvas.getObjects().forEach(obj => {
              obj.set({
                left:   (obj.left / savedW) * cx,
                top:    (obj.top  / savedH) * cy,
                scaleX: obj.scaleX * s,
                scaleY: obj.scaleY * s,
              });
              obj.setCoords();
            });
            this.fitObjectsToCanvas();
          }
          this.reapplyPremiumFonts();
          this.canvas.renderAll();
          this.onSelectionChange();
        });
      } else {
        this.canvas.clear();
        this.canvas.renderAll();
        this.onSelectionChange();
      }
    },

    // ================================================================
    // FILE UPLOAD
    // ================================================================
    bindFileUpload() {
      const fileInput = document.getElementById('tsd-file-input');
      const uploadZone = document.getElementById('tsd-upload-zone');

      if (!fileInput || !uploadZone) return;

      // Click on zone → trigger file input. The "Browse Files" label already
      // opens the picker natively via its `for` attribute, so let its click
      // bubble past untouched — calling click() again here opens the dialog a
      // second time and Chrome drops the change event for the chosen file.
      uploadZone.addEventListener('click', (e) => {
        if (e.target.closest('.tsd-thumb-remove')) return;
        if (e.target.closest('.tsd-upload-btn')) return;
        fileInput.click();
      });

      fileInput.addEventListener('change', (e) => {
        Array.from(e.target.files).forEach(file => this.processImageFile(file));
        fileInput.value = '';
      });

      // Drag & drop
      uploadZone.addEventListener('dragover', (e) => {
        e.preventDefault();
        uploadZone.classList.add('tsd-drag-over');
      });
      uploadZone.addEventListener('dragleave', () => {
        uploadZone.classList.remove('tsd-drag-over');
      });
      uploadZone.addEventListener('drop', (e) => {
        e.preventDefault();
        uploadZone.classList.remove('tsd-drag-over');
        Array.from(e.dataTransfer.files)
          .filter(f => f.type.startsWith('image/'))
          .forEach(f => this.processImageFile(f));
      });
    },

    processImageFile(file) {
      if (!file.type.startsWith('image/')) return;
      if (file.size > 10 * 1024 * 1024) {
        this.tsdAlert('That image is over 10MB. Please choose a smaller file.', { title: 'Image too large', icon: 'error' });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        this.addImageToCanvas(dataUrl);
        this.addToUploadedList(dataUrl);
        this.uploadArtwork(dataUrl, file);
      };
      reader.readAsDataURL(file);
    },

    // Uploads the untouched source file to imgBB so the print team gets the
    // original artwork, not the downscaled composite. Starts at drop time to
    // overlap with the customer still designing.
    //
    // The artwork IS the product, so this must not fail quietly: three attempts
    // with backoff, and add-to-cart is blocked outright if any image on the
    // canvas still has no URL (see collectArtworkProps).
    uploadArtwork(dataUrl, file) {
      const existing = this._artwork.get(dataUrl);
      if (existing && (existing.url || existing.inFlight)) return existing.promise;

      const record = this._artwork.get(dataUrl)
                  || { promise: null, url: '', name: file?.name || '', file: file || null };
      record.inFlight = true;
      if (file) record.file = file;

      record.promise = (async () => {
        // Attempts 1-2 post the File; the last falls back to the base64 the
        // canvas holds, which covers a File reference gone stale (and is the
        // same original bytes — FileReader read the whole file, not a resize).
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            const body = new FormData();
            body.append('key', IMGBB_KEY);

            if (attempt < 3 && record.file) {
              body.append('image', record.file);
            } else {
              body.append('image', String(dataUrl).replace(/^data:image\/\w+;base64,/, ''));
            }
            if (record.name) body.append('name', record.name.replace(/\.[^.]+$/, ''));

            const controller = new AbortController();
            const timer      = setTimeout(() => controller.abort(), 30000);
            const res        = await fetch('https://api.imgbb.com/1/upload', {
              method: 'POST', body, signal: controller.signal,
            });
            clearTimeout(timer);

            const json = await res.json();
            const url  = json?.data?.image?.url || json?.data?.url || json?.data?.display_url || '';
            if (url) { record.url = url; break; }
          } catch (_) { /* fall through to retry */ }

          if (attempt < 3) await new Promise(r => setTimeout(r, attempt * 1500));
        }

        record.inFlight = false;
        return record.url;
      })();

      this._artwork.set(dataUrl, record);
      return record.promise;
    },

    // Returns { props, missing } — "Artwork Front"-style properties for the
    // order, plus a count of canvas images with no URL. A non-zero `missing`
    // must block add-to-cart: an order without its artwork is unprintable.
    async collectArtworkProps() {
      const pending = Array.from(this._artwork.values())
                           .filter(r => r.inFlight).map(r => r.promise);
      if (pending.length) await Promise.allSettled(pending);

      // Last-ditch retry for anything that exhausted its attempts earlier.
      const failed = Array.from(this._artwork.entries()).filter(([, r]) => !r.url);
      if (failed.length) {
        await Promise.allSettled(failed.map(([src, r]) => this.uploadArtwork(src, r.file)));
      }

      const props = {};
      let missing = 0;

      ['front', 'back'].forEach((view) => {
        const objects = this.designs[view]?.objects || [];
        let n = 0;
        objects.forEach((obj) => {
          const src = obj._uploadDataUrl;
          if (!src) return;
          n += 1;
          const label = view === 'front' ? 'Artwork Front' : 'Artwork Back';
          const url   = this._artwork.get(src)?.url;
          if (url) props[n === 1 ? label : `${label} ${n}`] = url;
          else missing += 1;
        });
      });

      return { props, missing };
    },

    // Centres an uploaded object and scales it to fit inside the print area,
    // never enlarging past its natural size. Shared by the raster and SVG paths.
    placeUploadOnCanvas(obj, dataUrl) {
      const maxW = this.canvas.width * 0.75;
      const maxH = this.canvas.height * 0.75;
      const scale = Math.min(
        maxW / obj.width,
        maxH / obj.height,
        1
      );

      obj.set({
        left:    this.canvas.width / 2,
        top:     this.canvas.height / 2,
        originX: 'center',
        originY: 'center',
        scaleX:  scale,
        scaleY:  scale,
      });
      obj.id = 'img-' + Date.now();
      obj._uploadDataUrl = dataUrl;

      this.canvas.add(obj);
      this.canvas.setActiveObject(obj);
      this.canvas.renderAll();
      this.onSelectionChange();
    },

    // Turns an uploaded data URL into a sized Fabric object, then hands it to
    // `cb`. Calls back with null (after alerting) if the file can't be used, so
    // callers only ever deal with something that has real dimensions.
    //
    // SVGs need the vector path: loaded through fabric.Image (i.e. as an <img>)
    // they report a natural size of 0 unless they carry explicit width/height
    // attributes — a viewBox alone isn't enough — and land invisible.
    _loadUploadObject(dataUrl, cb) {
      const fail = (msg) => {
        this.tsdAlert(msg, { title: 'Upload failed', icon: 'error' });
        cb(null);
      };

      if (/^data:image\/svg\+xml/i.test(dataUrl)) {
        fabric.loadSVGFromURL(dataUrl, (objects, options) => {
          if (!objects || !objects.length) {
            return fail('That SVG couldn’t be read. Please try a PNG or JPG instead.');
          }
          const svg = fabric.util.groupSVGElements(objects, options);
          if (!svg.width || !svg.height) {
            return fail('That SVG has no usable size. Please try a PNG or JPG instead.');
          }
          cb(svg);
        });
        return;
      }

      fabric.Image.fromURL(dataUrl, (img) => {
        // A bitmap that failed to decode reports 0×0, which would collapse the
        // scale maths into an invisible object sitting silently on the canvas.
        if (!img || !img.width || !img.height) {
          return fail('That image couldn’t be read. Please try a different file.');
        }
        cb(img);
      }, { crossOrigin: 'anonymous' });
    },

    addImageToCanvas(dataUrl) {
      this._loadUploadObject(dataUrl, (obj) => {
        if (obj) this.placeUploadOnCanvas(obj, dataUrl);
      });
    },

    addToUploadedList(dataUrl) {
      const list = document.getElementById('tsd-uploaded-list');
      if (!list) return;

      const thumb = document.createElement('div');
      thumb.className = 'tsd-uploaded-thumb';

      const img = document.createElement('img');
      img.src = dataUrl;
      img.alt = 'Uploaded image';

      const removeBtn = document.createElement('button');
      removeBtn.className = 'tsd-thumb-remove';
      removeBtn.title = 'Remove';
      removeBtn.innerHTML = '×';
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        thumb.remove();
      });

      // Click thumb: select existing instance on canvas, or re-add if deleted
      thumb.addEventListener('click', () => {
        const existing = this.canvas.getObjects().find(o => o._uploadDataUrl === dataUrl);
        if (existing) {
          this.canvas.setActiveObject(existing);
          this.canvas.renderAll();
          this.onSelectionChange();
        } else {
          this.addImageToCanvas(dataUrl);
        }
      });

      const badge = document.createElement('span');
      badge.className = 'tsd-thumb-placement';
      badge.textContent = this.currentView === 'back' ? 'Back' : 'Front';

      thumb.appendChild(img);
      thumb.appendChild(removeBtn);
      thumb.appendChild(badge);
      list.appendChild(thumb);

      this.uploadedImages.push({ dataUrl, thumb });
    },

    // ================================================================
    // TEXT CONTROLS
    // ================================================================
    bindTextControls() {
      const boldBtn      = document.getElementById('tsd-bold');
      const italicBtn    = document.getElementById('tsd-italic');
      const underlineBtn = document.getElementById('tsd-underline');
      const addTextBtn   = document.getElementById('tsd-btn-add-text');
      const colourInput  = document.getElementById('tsd-text-colour');
      const colourValue  = document.getElementById('tsd-colour-value');

      if (!addTextBtn) return;

      // Bold / Italic / Underline toggles
      [boldBtn, italicBtn, underlineBtn].forEach(btn => {
        if (btn) btn.addEventListener('click', () => {
          btn.classList.toggle('tsd-style-btn--active');
          this.updateActiveTextStyle();
          this._updateLivePreview();
        });
      });

      // Colour change — listen to both events for cross-browser compatibility
      if (colourInput) {
        const applyColour = () => {
          this._userPickedTextColour = true;
          if (colourValue) colourValue.textContent = colourInput.value.toUpperCase();
          this.updateActiveTextStyle();
          this._updateLivePreview();
        };
        colourInput.addEventListener('input', applyColour);
        colourInput.addEventListener('change', applyColour);
      }

      // Live update on font/size changes
      ['tsd-font-family', 'tsd-font-size'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.addEventListener('change', () => {
          // Picking a normal font family from the dropdown leaves premium mode
          if (id === 'tsd-font-family' && this._pendingPremium) {
            this._pendingPremium = '';
            this._setColourLock(false);
            this._syncPremiumButtons('');
            const a = this.canvas.getActiveObject();
            if (a && this._isPremiumObj(a)) {
              // convert the baked premium image back to editable text in the chosen font
              this._convertPremiumToText(a);
              const t = this.canvas.getActiveObject();
              if (t) t.set('fontFamily', el.value);
              this.canvas.renderAll();
              return;
            }
          }
          this.updateActiveTextStyle();
          this._updateLivePreview();
        });
      });

      // Alignment
      document.querySelectorAll('.tsd-align-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          document.querySelectorAll('.tsd-align-btn').forEach(b => b.classList.remove('tsd-align-btn--active'));
          btn.classList.add('tsd-align-btn--active');
          const active = this.canvas.getActiveObject();
          if (active && (active.type === 'text' || active.type === 'i-text')) {
            active.set('textAlign', btn.dataset.align);
            this.canvas.renderAll();
          }
          this._updateLivePreview();
        });
      });

      // Live preview: update canvas every keystroke
      const textInput = document.getElementById('tsd-text-input');
      if (textInput) {
        textInput.addEventListener('input',   () => {
          // Typing again means this is new text, not the one already committed.
          this._textCommitted = false;
          this._updateLivePreview();
        });
        textInput.addEventListener('keydown', (e) => {
          // Enter inserts a line break (textarea default). Commit with the
          // Add Text button, or Ctrl/Cmd+Enter for keyboard users.
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            this.addTextToCanvas();
          }
        });
      }

      // Commit on button click
      addTextBtn.addEventListener('click', () => this.addTextToCanvas());

      // Arc text button
      const arcBtn = document.getElementById('tsd-btn-add-arc-text');
      if (arcBtn) arcBtn.addEventListener('click', () => this.addArcTextToCanvas());

      // If the live-preview object is deleted from canvas externally, clear the ref
      this.canvas.on('object:removed', (e) => {
        if (e.target === this._livePreviewObj) this._livePreviewObj = null;
      });
    },

    // Reads current text-input value + style controls and creates or updates
    // a live-preview text object on the canvas.
    _updateLivePreview() {
      const textInput = document.getElementById('tsd-text-input');
      const raw  = textInput ? textInput.value : '';
      const text = raw || '';

      if (!text) {
        if (this._livePreviewObj) {
          this.canvas.remove(this._livePreviewObj);
          this._livePreviewObj = null;
          this.canvas.discardActiveObject();
          this.canvas.renderAll();
          this.onSelectionChange();
        }
        return;
      }

      // The text in the box has already been added to the canvas and the box was
      // left populated on purpose. Style changes (colour, size, font) must edit
      // the selected object via updateActiveTextStyle() — not spawn a duplicate.
      if (this._textCommitted && !this._livePreviewObj) return;

      const fontFamily = document.getElementById('tsd-font-family')?.value || 'Arial';
      const fontSize   = Math.max(8, Math.min(200, parseInt(document.getElementById('tsd-font-size')?.value) || 40));
      const fill       = document.getElementById('tsd-text-colour')?.value || '#000000';
      const fontWeight = document.getElementById('tsd-bold')?.classList.contains('tsd-style-btn--active') ? 'bold' : 'normal';
      const fontStyle  = document.getElementById('tsd-italic')?.classList.contains('tsd-style-btn--active') ? 'italic' : 'normal';
      const underline  = !!document.getElementById('tsd-underline')?.classList.contains('tsd-style-btn--active');
      const alignBtn   = document.querySelector('.tsd-align-btn--active');
      const textAlign  = alignBtn ? (alignBtn.dataset.align || 'left') : 'left';

      // Premium colour font selected -> the preview is a baked atlas image
      if (this._pendingPremium) {
        this._updatePremiumLivePreview(this._pendingPremium, text, fontSize);
        return;
      }
      // Previous preview was a premium image (premium just turned off) -> drop it
      if (this._livePreviewObj && this._isPremiumObj(this._livePreviewObj)) {
        this.canvas.remove(this._livePreviewObj);
        this._livePreviewObj = null;
      }

      if (this._livePreviewObj) {
        // Update existing preview in place
        this._livePreviewObj.set({ text, fontFamily, fontSize, fill, fontWeight, fontStyle, underline, textAlign });
        this._livePreviewObj.setCoords();
        this.canvas.setActiveObject(this._livePreviewObj);
        this.canvas.renderAll();
      } else {
        // First keystroke — create the preview object centred on canvas
        const obj = new fabric.Text(text, {
          left: this.canvas.width  / 2,
          top:  this.canvas.height / 2,
          originX: 'center',
          originY: 'center',
          fontFamily, fontSize, fill, fontWeight, fontStyle, underline, textAlign,
        });
        obj.id = '_live_preview';
        this._livePreviewObj = obj;
        this.canvas.add(obj);
        this.canvas.setActiveObject(obj);
        this.canvas.renderAll();
        this.onSelectionChange();
      }
    },

    addTextToCanvas() {
      const textInput = document.getElementById('tsd-text-input');
      const text = textInput ? textInput.value.trim() : '';
      if (!text) {
        textInput && textInput.focus();
        return;
      }
      // Already committed and nothing retyped — don't add the same text twice.
      if (this._textCommitted && !this._livePreviewObj) {
        textInput && textInput.select();
        return;
      }

      if (this._livePreviewObj) {
        // Preview is already on canvas — just commit it (assign a real id, clear ref).
        // Premium previews are baked images; don't poke a text prop into them.
        if (!this._isPremiumObj(this._livePreviewObj)) this._livePreviewObj.set({ text });
        this._livePreviewObj.id = 'text-' + Date.now();
        this._livePreviewObj.setCoords();
        this.canvas.setActiveObject(this._livePreviewObj);
        this.canvas.renderAll();
        this._livePreviewObj = null;
      } else if (this._pendingPremium) {
        // Fallback: no live preview but a premium font is selected — bake the image
        const fontSize = Math.max(8, Math.min(200, parseInt(document.getElementById('tsd-font-size')?.value) || 40));
        this._makePremiumImage(this._pendingPremium, text, fontSize,
          { left: this.canvas.width / 2, top: this.canvas.height / 2, originX: 'center', originY: 'center' },
          (img) => {
            if (!img) return;
            img.id = 'text-' + Date.now();
            this.canvas.add(img);
            this.canvas.setActiveObject(img);
            this.canvas.renderAll();
            this.onSelectionChange();
            this._debouncePushHistory && this._debouncePushHistory();
          });
      } else {
        // Fallback: no live preview, create fresh
        const fontFamily = document.getElementById('tsd-font-family')?.value || 'Arial';
        const fontSize   = parseInt(document.getElementById('tsd-font-size')?.value) || 40;
        const fill       = document.getElementById('tsd-text-colour')?.value || '#000000';
        const fontWeight = document.getElementById('tsd-bold')?.classList.contains('tsd-style-btn--active') ? 'bold' : 'normal';
        const fontStyle  = document.getElementById('tsd-italic')?.classList.contains('tsd-style-btn--active') ? 'italic' : 'normal';
        const underline  = !!document.getElementById('tsd-underline')?.classList.contains('tsd-style-btn--active');
        const alignBtn   = document.querySelector('.tsd-align-btn--active');
        const textAlign  = alignBtn ? (alignBtn.dataset.align || 'left') : 'left';

        const textObj = new fabric.Text(text, {
          left: this.canvas.width  / 2,
          top:  this.canvas.height / 2,
          originX: 'center',
          originY: 'center',
          fontFamily,
          fontSize:   Math.max(8, Math.min(200, fontSize)),
          fill, fontWeight, fontStyle, underline, textAlign,
        });
        textObj.id = 'text-' + Date.now();
        this.canvas.add(textObj);
        this.canvas.setActiveObject(textObj);
        this.canvas.renderAll();
        this.onSelectionChange();
      }

      // Keep the committed text in the box so the customer can still see (and
      // tweak) what they typed. It's selected, so typing replaces it — which is
      // what lets them go on to add a second, different text.
      this._textCommitted = true;
      if (textInput) {
        textInput.focus();
        textInput.select();
      }
    },

    updateActiveTextStyle() {
      const active = this.canvas.getActiveObject();

      // Premium colour-font image: only the font SIZE applies (it rescales the
      // baked artwork). Colour / weight / style are owned by the font itself.
      if (this._isPremiumObj(active)) {
        const fs = parseInt(document.getElementById('tsd-font-size')?.value);
        if (!isNaN(fs) && fs > 0) this._resizePremiumImage(active, fs);
        this.canvas.renderAll();
        return;
      }

      if (!active || (active.type !== 'text' && active.type !== 'i-text')) return;

      const fontFamily = document.getElementById('tsd-font-family')?.value;
      const fontSize   = parseInt(document.getElementById('tsd-font-size')?.value);
      const fill       = document.getElementById('tsd-text-colour')?.value;
      const fontWeight = document.getElementById('tsd-bold')?.classList.contains('tsd-style-btn--active') ? 'bold' : 'normal';
      const fontStyle  = document.getElementById('tsd-italic')?.classList.contains('tsd-style-btn--active') ? 'italic' : 'normal';
      const underline  = document.getElementById('tsd-underline')?.classList.contains('tsd-style-btn--active') || false;

      if (fontFamily) active.set('fontFamily', fontFamily);
      if (!isNaN(fontSize) && fontSize > 0) active.set('fontSize', fontSize);
      if (fill) active.set('fill', fill);
      active.set({ fontWeight, fontStyle, underline });

      this.canvas.renderAll();
    },

    // ================================================================
    // OBJECT CONTROLS
    // ================================================================
    bindObjectControls() {
      const deleteBtn  = document.getElementById('tsd-btn-delete');
      const forwardBtn = document.getElementById('tsd-btn-forward');
      const backBtn    = document.getElementById('tsd-btn-backward');
      const clearBtn   = document.getElementById('tsd-btn-clear');

      if (deleteBtn) {
        deleteBtn.addEventListener('click', () => {
          const active = this.canvas.getActiveObject();
          if (!active) return;
          if (active.type === 'activeSelection') {
            active.getObjects().forEach(o => this.canvas.remove(o));
            this.canvas.discardActiveObject();
          } else {
            this.canvas.remove(active);
          }
          this.canvas.renderAll();
          this.onSelectionChange();
        });
      }

      if (forwardBtn) {
        forwardBtn.addEventListener('click', () => {
          const active = this.canvas.getActiveObject();
          if (active) { this.canvas.bringForward(active); this.canvas.renderAll(); }
        });
      }

      if (backBtn) {
        backBtn.addEventListener('click', () => {
          const active = this.canvas.getActiveObject();
          if (active) { this.canvas.sendBackwards(active); this.canvas.renderAll(); }
        });
      }

      if (clearBtn) {
        clearBtn.addEventListener('click', async () => {
          if (this.canvas.getObjects().length === 0) return;
          const ok = await this.tsdConfirm('This removes everything you’ve added to the current side. This can’t be undone.', {
            title: 'Clear this design?', icon: 'warning', okLabel: 'Clear design', cancelLabel: 'Keep editing',
          });
          if (!ok) return;
          this.canvas.clear();
          this.canvas.renderAll();
          this.onSelectionChange();
        });
      }
    },

    onSelectionChange() {
      const active    = this.canvas.getActiveObject();
      const hasObj    = !!active;
      const deleteBtn = document.getElementById('tsd-btn-delete');

      if (deleteBtn) deleteBtn.disabled = !hasObj;

      this.updateInspector();
      this.syncTextControls(active);

      // Auto-switch to Text tab when a text / premium object is selected
      if (active && (active.type === 'text' || active.type === 'i-text' || this._isPremiumObj(active))) {
        this._activateTab('text');
      }
    },

    _activateTab(tabName) {
      document.querySelectorAll('.tsd-tab').forEach(t => {
        t.classList.toggle('tsd-tab--active', t.dataset.tab === tabName);
        t.setAttribute('aria-selected', t.dataset.tab === tabName ? 'true' : 'false');
      });
      document.querySelectorAll('.tsd-tab-panel').forEach(p => {
        p.classList.toggle('tsd-tab-panel--active', p.id === 'tsd-tab-' + tabName);
      });
    },

    syncTextControls(obj) {
      // Premium colour-font image: reflect its font + size, lock the colour.
      if (this._isPremiumObj(obj)) {
        this._pendingPremium = obj._premiumKey;
        this._syncPremiumButtons(obj._premiumKey);
        this._setColourLock(true);
        const sizeEl = document.getElementById('tsd-font-size');
        if (sizeEl) sizeEl.value = Math.round(obj._premiumFontSize || 40);
        return;
      }

      if (!obj || (obj.type !== 'text' && obj.type !== 'i-text')) return;

      const fontFamilyEl = document.getElementById('tsd-font-family');
      const fontSizeEl   = document.getElementById('tsd-font-size');
      const colourEl     = document.getElementById('tsd-text-colour');
      const colourValEl  = document.getElementById('tsd-colour-value');
      const boldBtn      = document.getElementById('tsd-bold');
      const italicBtn    = document.getElementById('tsd-italic');
      const underlineBtn = document.getElementById('tsd-underline');

      if (fontFamilyEl) fontFamilyEl.value = obj.fontFamily || 'Arial';
      if (fontSizeEl)   fontSizeEl.value   = Math.round(obj.fontSize) || 40;

      // Reflect the object's premium font (or lack of) in the picker + colour lock.
      const premium = obj._premium || '';
      this._pendingPremium = premium;
      this._syncPremiumButtons(premium);
      this._setColourLock(!!premium);

      // Only mirror a solid (string) fill into the colour input — premium fills
      // are Pattern/Gradient objects and must not be stringified into it.
      const solidFill = (typeof obj.fill === 'string') ? obj.fill : '#000000';
      if (!premium) {
        if (colourEl)    colourEl.value = solidFill;
        if (colourValEl) colourValEl.textContent = solidFill.toUpperCase();
      }

      if (boldBtn)      boldBtn.classList.toggle('tsd-style-btn--active', obj.fontWeight === 'bold');
      if (italicBtn)    italicBtn.classList.toggle('tsd-style-btn--active', obj.fontStyle === 'italic');
      if (underlineBtn) underlineBtn.classList.toggle('tsd-style-btn--active', !!obj.underline);
    },

    updateInspector() {
      const inspector     = document.getElementById('tsd-inspector');
      const inspectorBody = document.getElementById('tsd-inspector-body');
      const active        = this.canvas.getActiveObject();

      if (!inspector || !inspectorBody) return;

      if (!active) {
        inspector.hidden = true;
        return;
      }

      inspector.hidden = false;
      const x = Math.round(active.left);
      const y = Math.round(active.top);
      const w = Math.round(active.getScaledWidth());
      const h = Math.round(active.getScaledHeight());
      const rot = Math.round(active.angle);

      const isText = active.type === 'text' || active.type === 'i-text';
      const fillHex = isText ? (this._ensureHex(active.fill) || '#000000') : null;

      inspectorBody.innerHTML = `
        <div class="tsd-inspector-row">
          <label>X / Y</label>
          <span>${x}px &nbsp; ${y}px</span>
        </div>
        <div class="tsd-inspector-row">
          <label>W × H</label>
          <span>${w}px × ${h}px</span>
        </div>
        <div class="tsd-inspector-row">
          <label>Rotation</label>
          <span>${rot}°</span>
        </div>
        ${isText ? `
        <div class="tsd-inspector-row">
          <label>Font</label>
          <span>${active.fontFamily}</span>
        </div>
        <div class="tsd-inspector-row tsd-inspector-row--colour">
          <label>Colour</label>
          <div class="tsd-colour-field">
            <input type="color" id="tsd-inspector-colour" value="${fillHex}">
            <span class="tsd-colour-value" id="tsd-inspector-colour-val">${fillHex.toUpperCase()}</span>
          </div>
        </div>
        <div class="tsd-inspector-row tsd-inspector-row--opacity">
          <label>Opacity</label>
          <input type="range" id="tsd-inspector-opacity" class="tsd-opacity-slider" min="0" max="100" value="${Math.round((active.opacity ?? 1) * 100)}">
          <span id="tsd-inspector-opacity-val">${Math.round((active.opacity ?? 1) * 100)}%</span>
        </div>` : `
        <div class="tsd-inspector-row tsd-inspector-row--opacity">
          <label>Opacity</label>
          <input type="range" id="tsd-inspector-opacity" class="tsd-opacity-slider" min="0" max="100" value="${Math.round((active.opacity ?? 1) * 100)}">
          <span id="tsd-inspector-opacity-val">${Math.round((active.opacity ?? 1) * 100)}%</span>
        </div>`}
      `;

      // Bind inspector colour picker (text objects)
      const inspClrEl = document.getElementById('tsd-inspector-colour');
      if (inspClrEl) {
        inspClrEl.addEventListener('input', () => {
          const obj = this.canvas.getActiveObject();
          if (!obj) return;
          this._userPickedTextColour = true;
          obj.set('fill', inspClrEl.value);
          this.canvas.renderAll();
          const valEl = document.getElementById('tsd-inspector-colour-val');
          if (valEl) valEl.textContent = inspClrEl.value.toUpperCase();
          // Keep text-tab colour picker in sync
          const textClrEl = document.getElementById('tsd-text-colour');
          const textClrValEl = document.getElementById('tsd-colour-value');
          if (textClrEl) textClrEl.value = inspClrEl.value;
          if (textClrValEl) textClrValEl.textContent = inspClrEl.value.toUpperCase();
        });
      }

      // Bind opacity slider (all objects)
      const opacityEl = document.getElementById('tsd-inspector-opacity');
      if (opacityEl) {
        opacityEl.addEventListener('input', () => {
          const obj = this.canvas.getActiveObject();
          if (!obj) return;
          const pct = parseInt(opacityEl.value);
          obj.set('opacity', pct / 100);
          this.canvas.renderAll();
          const valEl = document.getElementById('tsd-inspector-opacity-val');
          if (valEl) valEl.textContent = pct + '%';
        });
      }
    },

    // Normalise any fill value to a 6-digit hex string for colour inputs
    _ensureHex(fill) {
      if (!fill || typeof fill !== 'string') return '#000000';
      if (fill.startsWith('#') && (fill.length === 7 || fill.length === 4)) return this.cssColorToHex(fill);
      return this.cssColorToHex(fill);
    },

    // ================================================================
    // TABS
    // ================================================================
    bindTabs() {
      document.querySelectorAll('.tsd-tab').forEach(tab => {
        tab.addEventListener('click', () => {
          document.querySelectorAll('.tsd-tab').forEach(t => {
            t.classList.remove('tsd-tab--active');
            t.setAttribute('aria-selected', 'false');
          });
          document.querySelectorAll('.tsd-tab-panel').forEach(p => {
            p.classList.remove('tsd-tab-panel--active');
          });

          tab.classList.add('tsd-tab--active');
          tab.setAttribute('aria-selected', 'true');

          const panel = document.getElementById('tsd-tab-' + tab.dataset.tab);
          if (panel) panel.classList.add('tsd-tab-panel--active');
        });
      });
    },

    // ================================================================
    // CLIPART
    // ================================================================
    buildClipart() {
      const grid = document.getElementById('tsd-clipart-grid');
      if (!grid) return;
      grid.innerHTML = '';

      const items = this.CLIPART.filter(item => item.cat === this._clipartCat);
      items.forEach(item => {
        const btn = document.createElement('button');
        btn.className = 'tsd-clipart-item';
        btn.type = 'button';
        btn.title = item.label;
        btn.setAttribute('aria-label', item.label);

        const svgAttrs = item.fill
          ? 'fill="currentColor" stroke="none"'
          : 'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" ${svgAttrs}>${item.icon}</svg>`;

        btn.addEventListener('click', () => this.addClipartToCanvas(item));
        grid.appendChild(btn);
      });
    },

    bindClipartCategories() {
      document.querySelectorAll('.tsd-clipart-cat').forEach(btn => {
        btn.addEventListener('click', () => {
          document.querySelectorAll('.tsd-clipart-cat').forEach(b => b.classList.remove('tsd-clipart-cat--active'));
          btn.classList.add('tsd-clipart-cat--active');
          this._clipartCat = btn.dataset.cat;
          this.buildClipart();
        });
      });
    },

    addClipartToCanvas(item) {
      const sw    = item.fill ? '0' : '1.5';
      const fill  = item.fill ? 'black' : 'none';
      const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${fill}" stroke="black" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round">${item.icon}</svg>`;

      // Stagger so multiple items don't all land in the exact same spot
      const stagger = (this.canvas.getObjects().length % 6) * 12;

      fabric.loadSVGFromString(svgStr, (objects, options) => {
        if (!objects || objects.length === 0) return;
        const group = fabric.util.groupSVGElements(objects, options);

        // Scale to ~80 canvas pixels regardless of original SVG size
        const targetPx = Math.min(this.canvas.width, this.canvas.height) * 0.35;
        const naturalSize = Math.max(group.width || 24, group.height || 24);
        const s = targetPx / naturalSize;

        group.set({
          left:    this.canvas.width  / 2 + stagger,
          top:     this.canvas.height / 2 + stagger,
          originX: 'center',
          originY: 'center',
          scaleX:  s,
          scaleY:  s,
        });
        group.id = 'clipart-' + Date.now();

        this.canvas.add(group);
        this.canvas.setActiveObject(group);
        this.canvas.renderAll();
        this.onSelectionChange();
      });
    },

    // ================================================================
    // TEMPLATES
    // ================================================================
    buildTemplates() {
      const grid = document.getElementById('tsd-template-grid');
      if (!grid) return;

      this.TEMPLATES.forEach(tpl => {
        const btn = document.createElement('button');
        btn.className = 'tsd-template-item';
        btn.type = 'button';
        btn.title = tpl.label;
        btn.setAttribute('aria-label', tpl.label);
        btn.innerHTML = `
          <div class="tsd-template-preview">${tpl.preview}</div>
          <span class="tsd-template-item-label">${tpl.label}</span>
        `;
        btn.addEventListener('click', () => this.onTemplateClick(tpl));
        grid.appendChild(btn);
      });
    },

    // Scale down any objects that overflow the canvas, keeping them centred.
    // Called after template apply and after loadView rescaling.
    fitObjectsToCanvas() {
      const W = this.canvas.width;
      const H = this.canvas.height;
      let changed = false;
      this.canvas.getObjects().forEach(obj => {
        obj.setCoords();
        const bb = obj.getBoundingRect();
        const ratioW = bb.width  / (W * 0.96);
        const ratioH = bb.height / (H * 0.96);
        const overRatio = Math.max(ratioW, ratioH);
        if (overRatio > 1) {
          const fit = 1 / overRatio;
          obj.set({ scaleX: obj.scaleX * fit, scaleY: obj.scaleY * fit });
          // Re-centre after scaling (most template objects use originX:'center')
          if (obj.originX === 'center') obj.left = W / 2;
          if (obj.originY === 'center') obj.top  = H / 2;
          obj.setCoords();
          changed = true;
        }
      });
      if (changed) this.canvas.renderAll();
    },

    applyTemplate(tpl) {
      const gen = ++this._tplGen;
      const isCurrent = () => this._tplGen === gen;
      const isDark = this.isColourDark(this.shirtHex);
      this.canvas.clear();
      this.canvas.renderAll();
      tpl.apply(this.canvas, this.canvas.width, this.canvas.height, isDark, isCurrent);
      // Use two rAF frames: first lets async SVG templates (heart, badge, etc.)
      // finish adding objects, second lets Fabric measure text bounding boxes.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.fitObjectsToCanvas();
        const firstText = this.canvas.getObjects().find(o => o.type === 'text' || o.type === 'i-text');
        if (firstText) {
          this.canvas.setActiveObject(firstText);
          this.canvas.renderAll();
        }
        this.onSelectionChange();
      }));
    },

    // Entry point for a template card click. Image-based templates first ask
    // the customer to add their own photo so it drops straight into the layout.
    async onTemplateClick(tpl) {
      if (!tpl.needsImage) { this.applyTemplate(tpl); return; }

      const isLogo = tpl.imageKind === 'logo';
      const useOwnPhoto = await this.tsdConfirm(
        isLogo
          ? 'This design is built around your company logo. Add it now to drop it straight into the layout — you can also upload later from the Upload tab.'
          : 'This design is built around your own photo. Add one now to drop it straight into the layout — you can also upload later from the Upload tab.',
        isLogo
          ? { title: 'Add your logo', icon: 'photo', okLabel: 'Choose logo' }
          : { title: 'Add your photo', icon: 'photo', okLabel: 'Choose photo' }
      );

      // Lay down the template either way so the customer sees the layout.
      this.applyTemplate(tpl);
      if (!useOwnPhoto) return;

      const file = await this._pickImageFile();
      if (file) this._fillTemplateFrameFromFile(file);
    },

    // Opens a native file chooser and resolves with the selected File (or null).
    _pickImageFile() {
      return new Promise((resolve) => {
        const inp = document.createElement('input');
        inp.type = 'file';
        inp.accept = 'image/*';
        inp.style.display = 'none';
        document.body.appendChild(inp);
        inp.addEventListener('change', () => {
          const file = inp.files && inp.files[0] ? inp.files[0] : null;
          inp.remove();
          resolve(file);
        }, { once: true });
        inp.click();
      });
    },

    // Validates a chosen file then places it inside the template's photo frame.
    _fillTemplateFrameFromFile(file) {
      if (!file.type.startsWith('image/')) {
        this.tsdToast('That file isn’t an image. Please choose a JPG, PNG or SVG.', { type: 'error' });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        this.tsdAlert('That image is over 10MB. Please choose a smaller file.', { title: 'Image too large', icon: 'error' });
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        this._placeImageInFrame(e.target.result);
        this.uploadArtwork(e.target.result, file);
      };
      reader.readAsDataURL(file);
    },

    // Fits an uploaded image into the template's tagged photo frame, removing the
    // dashed guide + "ADD PHOTO" hint. Circle frames clip the photo to a circle.
    _placeImageInFrame(dataUrl) {
      const frame = this.canvas.getObjects().find(o => o._tsdRole === 'photo-frame');
      if (!frame) { this.addImageToCanvas(dataUrl); this.addToUploadedList(dataUrl); return; }

      const hint     = this.canvas.getObjects().find(o => o._tsdRole === 'photo-hint');
      const isCircle = frame.type === 'circle';
      frame.setCoords();
      const fw = frame.getScaledWidth();
      const fh = frame.getScaledHeight();
      const cx = frame.left;   // template frames use originX/originY: 'center'
      const cy = frame.top;

      this._loadUploadObject(dataUrl, (img) => {
        if (!img) return;      // _loadUploadObject has already told the customer
        const scale = Math.min(fw / img.width, fh / img.height);
        img.set({
          left: cx, top: cy, originX: 'center', originY: 'center',
          scaleX: scale, scaleY: scale,
        });
        if (isCircle && !frame._tsdNoClip) {
          img.clipPath = new fabric.Circle({
            radius: Math.min(img.width, img.height) / 2,
            originX: 'center', originY: 'center',
          });
        }
        img.id = 'img-' + Date.now();
        img._uploadDataUrl = dataUrl;

        if (hint) this.canvas.remove(hint);
        this.canvas.remove(frame);
        this.canvas.add(img);
        this.canvas.setActiveObject(img);
        this.canvas.renderAll();
        this.onSelectionChange();
        this.addToUploadedList(dataUrl);
        if (typeof this._pushHistory === 'function') this._pushHistory();
      });
    },

    // Returns the ideal foreground colour for the current shirt
    shirtFg() {
      return this.isColourDark(this.shirtHex) ? '#ffffff' : '#000000';
    },

    // ================================================================
    // PRINT LOCATION PICKER
    // ================================================================
    bindLocationPicker() {
      document.querySelectorAll('.tsd-loc-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const locKey = btn.dataset.location;
          const posVal = btn.dataset.position || '';
          // Nothing to do only if BOTH location and position are unchanged
          // (distinct positions can share a location key, e.g. "Pocket + Back").
          if (locKey === this.currentLocation && posVal === this.currentPosition) return;

          const loc = this.PRINT_LOCATIONS[locKey];
          if (!loc) return;

          this.saveCurrentView();

          document.querySelectorAll('.tsd-loc-btn').forEach(b => b.classList.remove('tsd-loc-btn--active'));
          btn.classList.add('tsd-loc-btn--active');

          // Reveal the design canvas the first time a location is chosen
          document.getElementById('tsd-mockup-container')?.classList.remove('tsd-no-location');

          this.currentLocation = locKey;
          // Raw Print-Position variant value (e.g. "Front + Back") — drives variant
          // selection + price now that print position is the priced variant axis.
          this.currentPosition = posVal;
          this._updateVariantPrice(this._currentColour, this.currentPosition);

          // If current view isn't available for this location, switch to first valid side
          if (!loc.sides.includes(this.currentView)) {
            this.currentView = loc.sides[0];
          }

          this.syncViewToggleVisibility();

          // Update print area and mockup image for the new location + current view
          this.updatePrintAreaForCurrentView();
          this.swapMockupImage(this.currentView);

          // Load design for current view
          this.loadView(this.currentView);

          // Update label overlay
          const label = document.querySelector('.tsd-print-area-label');
          if (label) {
            const isDual = loc.sides.length > 1;
            label.textContent = this.currentView === 'front'
              ? (isDual ? 'Front Print Area' : 'Print Area')
              : 'Back Print Area';
          }
        });
      });

      // Seed the initial position value from the active location button so the
      // first variant match + price reflect the default print position.
      const activeLoc = document.querySelector('.tsd-loc-btn--active');
      if (activeLoc) this.currentPosition = activeLoc.dataset.position || '';
    },

    // Detect caps / beanies / bucket hats from the product type, tags or handle.
    // Cached after first call. The print area for these sits centred on the
    // small front panel rather than the t-shirt chest box.
    isHeadwear() {
      if (this._isHeadwear !== undefined) return this._isHeadwear;
      const hay = [
        window.tsdProductType   || '',
        window.tsdProductHandle || '',
        (window.tsdProductTags  || []).join(' '),
      ].join(' ').toLowerCase();
      this._isHeadwear = /\b(cap|caps|beanie|beanies|bucket hat|bucket-hat|hat|hats|headwear|snapback)\b/.test(hay)
        || /beanie|bucket-hat/.test(window.tsdProductHandle || '');
      return this._isHeadwear;
    },

    // Key for a dragged-area override: unique per print location + side, so
    // e.g. "Front Only" and "Pocket Only" keep their own positions.
    _areaOverrideKey() {
      return (this.currentLocation || '') + '|' + this.currentView;
    },

    updatePrintAreaForCurrentView() {
      const loc = this.PRINT_LOCATIONS[this.currentLocation];
      if (!loc) return;
      const area = loc.areas[this.currentView];
      if (!area) return;
      // Headwear starts from a centred panel default and may be dragged, so it
      // picks up any stored position (per location + side). Garments are fixed
      // to their per-location area and never take an override.
      const base = this.isHeadwear() ? { ...this.HEADWEAR_AREA } : { ...area };
      if (this.isHeadwear()) {
        const ov = this.areaOverride && this.areaOverride[this._areaOverrideKey()];
        if (ov) { base.topPct = ov.topPct; base.leftPct = ov.leftPct; }
      }
      this.PRINT_AREA = base;
      this.resizeCanvas();
    },

    // Add a drag handle so the customer can move the whole design area to
    // wherever the print needs to sit. Headwear only — see the isHeadwear()
    // gate at the call site. The position is stored per print location + side
    // and survives view/colour changes.
    enableAreaDrag() {
      const printArea = document.getElementById('tsd-print-area');
      const container = document.getElementById('tsd-mockup-container');
      if (!printArea || !container) return;
      if (printArea.querySelector('.tsd-area-drag-handle')) return; // already added

      this.areaOverride = this.areaOverride || {};

      // Drag handle (a grip badge pinned to the top-centre of the area)
      const handle = document.createElement('div');
      handle.className = 'tsd-area-drag-handle';
      handle.title = 'Drag to move the design area';
      handle.innerHTML =
        '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/></svg><span>Move area</span>';
      printArea.appendChild(handle);

      let dragging = false, startX = 0, startY = 0, startLeft = 0, startTop = 0;

      const point = (e) => (e.touches && e.touches[0]) ? e.touches[0] : e;

      const onDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragging = true;
        const pt = point(e);
        startX = pt.clientX;
        startY = pt.clientY;
        startLeft = parseFloat(printArea.style.left) || 0;
        startTop  = parseFloat(printArea.style.top)  || 0;
        handle.classList.add('is-dragging');
        document.addEventListener('mousemove', onMove, { passive: false });
        document.addEventListener('mouseup', onUp);
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onUp);
      };

      const onMove = (e) => {
        if (!dragging) return;
        e.preventDefault();
        const m = this._mockupMetrics;
        if (!m) return;
        const pt = point(e);
        const w = printArea.offsetWidth;
        const h = printArea.offsetHeight;
        // New top-left in container px, clamped so the area stays over the mockup
        let newLeft = startLeft + (pt.clientX - startX);
        let newTop  = startTop  + (pt.clientY - startY);
        newLeft = Math.max(m.imgOffsetX, Math.min(newLeft, m.imgOffsetX + m.imgW - w));
        newTop  = Math.max(m.imgOffsetY, Math.min(newTop,  m.imgOffsetY + m.imgH - h));
        printArea.style.left = newLeft + 'px';
        printArea.style.top  = newTop  + 'px';
        // Persist as percentages of the mockup image
        const leftPct = (newLeft - m.imgOffsetX) / m.imgW;
        const topPct  = (newTop  - m.imgOffsetY) / m.imgH;
        this.PRINT_AREA.leftPct = leftPct;
        this.PRINT_AREA.topPct  = topPct;
        this.areaOverride[this._areaOverrideKey()] = { leftPct, topPct };
      };

      const onUp = () => {
        dragging = false;
        handle.classList.remove('is-dragging');
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onUp);
      };

      handle.addEventListener('mousedown', onDown);
      handle.addEventListener('touchstart', onDown, { passive: false });
    },

    syncViewToggleVisibility() {
      const loc = this.PRINT_LOCATIONS[this.currentLocation];
      const isDual = loc && loc.sides.length > 1;
      const viewToggle = document.querySelector('.tsd-view-toggle');
      if (viewToggle) viewToggle.style.display = isDual ? '' : 'none';
      // Sync active button to currentView
      document.querySelectorAll('.tsd-view-btn').forEach(b => {
        b.classList.toggle('tsd-view-btn--active', b.dataset.view === this.currentView);
      });
    },

    swapMockupImage(view) {
      const frontSvg  = document.getElementById('tsd-shirt-front');
      const backSvg   = document.getElementById('tsd-shirt-back');
      const container = document.getElementById('tsd-mockup-container');

      if (frontSvg || backSvg) {
        // Inline SVG path
        if (view === 'back' && !backSvg) {
          const backUrl = container?.dataset.backUrl || '';
          if (backUrl) {
            this.fetchAndInjectSVG(backUrl, 'tsd-shirt-back', false).then(injected => {
              if (injected && this.shirtHex !== '#cccccc') {
                injected.querySelectorAll('.tsd-shirt-body').forEach(el => el.setAttribute('fill', this.shirtHex));
              }
              if (frontSvg) frontSvg.style.display = 'none';
              this.resizeCanvas();
            });
            return;
          }
        }
        if (frontSvg) frontSvg.style.display = view === 'back' ? 'none' : '';
        if (backSvg)  backSvg.style.display  = view === 'back' ? '' : 'none';
      } else {
        // JPEG/PNG fallback — swap the <img> src directly
        const mockupImg = document.getElementById('tsd-mockup-img');
        if (mockupImg) {
          const url = view === 'back'
            ? (container?.dataset.backUrl  || '')
            : (container?.dataset.frontUrl || '');
          if (url) {
            mockupImg.src = url;
            mockupImg.onload = () => {
              this.mockupNaturalW = mockupImg.naturalWidth  || 800;
              this.mockupNaturalH = mockupImg.naturalHeight || 800;
              this.resizeCanvas();
            };
          }
        }
      }
      this.resizeCanvas();
    },

    // ================================================================
    // FETCH + INJECT PRODUCT SVG INLINE
    // ================================================================
    async fetchAndInjectSVG(url, id, hidden) {
      if (!url) return null;
      // Ensure absolute URL (Shopify sometimes outputs protocol-relative //cdn.shopify.com/...)
      const absUrl = url.startsWith('//') ? 'https:' + url : url;
      try {
        const res = await fetch(absUrl, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) return null;
        const contentType = res.headers.get('content-type') || '';
        // Only proceed if response is SVG
        if (!contentType.includes('svg') && !contentType.includes('xml')) {
          const text = await res.text();
          if (!text.trimStart().startsWith('<') || !text.includes('<svg')) return null;
          // parse and inject below using text
          return this._parseSVGAndInject(text, id, hidden);
        }
        const text = await res.text();
        if (!text.includes('<svg')) return null;
        return this._parseSVGAndInject(text, id, hidden);
      } catch (e) {
        return null;
      }
    },

    _parseSVGAndInject(text, id, hidden) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'image/svg+xml');
      if (doc.querySelector('parsererror')) return null;
      const svgEl = doc.querySelector('svg');
      if (!svgEl) return null;

      svgEl.removeAttribute('width');
      svgEl.removeAttribute('height');
      svgEl.id = id;
      svgEl.classList.add('tsd-shirt-svg');
      if (hidden) svgEl.style.display = 'none';

      // Remove any existing element with same id before injecting
      document.getElementById(id)?.remove();

      const container = document.getElementById('tsd-mockup-container');
      const printArea  = document.getElementById('tsd-print-area');
      if (!container) return null;
      if (printArea) container.insertBefore(svgEl, printArea);
      else           container.appendChild(svgEl);

      this.markShirtBodyElements(svgEl);
      return svgEl;
    },

    markShirtBodyElements(svgEl) {
      // Scan all filled shapes; skip pure strokes, near-black outlines, near-white highlights
      const candidates = [];
      svgEl.querySelectorAll('path, rect, polygon, circle, ellipse, polyline').forEach(el => {
        const rawFill = el.getAttribute('fill');
        if (!rawFill || rawFill === 'none' || rawFill === 'transparent') return;
        const hex = this.cssColorToHex(rawFill);
        if (!hex) return;
        const r = parseInt(hex.slice(1,3), 16);
        const g = parseInt(hex.slice(3,5), 16);
        const b = parseInt(hex.slice(5,7), 16);
        const lum = (0.299*r + 0.587*g + 0.114*b) / 255;
        if (lum < 0.06 || lum > 0.94) return; // skip black outlines + white
        candidates.push({ el, fill: rawFill });
      });

      if (candidates.length === 0) {
        // Fallback: take the largest filled shape regardless of colour
        let largest = null, largestArea = 0;
        svgEl.querySelectorAll('path, rect, polygon, circle, ellipse').forEach(el => {
          if (!el.getAttribute('fill') || el.getAttribute('fill') === 'none') return;
          try {
            const bb = el.getBBox();
            if (bb.width * bb.height > largestArea) { largestArea = bb.width * bb.height; largest = el; }
          } catch (_) {}
        });
        if (largest) largest.classList.add('tsd-shirt-body');
        return;
      }

      // Group by fill; pick the colour with the most matching elements
      const groups = {};
      candidates.forEach(c => { (groups[c.fill] = groups[c.fill] || []).push(c.el); });
      let dominant = null, maxCount = 0;
      for (const [fill, els] of Object.entries(groups)) {
        if (els.length > maxCount) { maxCount = els.length; dominant = fill; }
      }
      if (dominant) groups[dominant].forEach(el => el.classList.add('tsd-shirt-body'));
    },

    cssColorToHex(color) {
      if (!color) return null;
      if (color.startsWith('#')) {
        if (color.length === 4) return '#'+color[1]+color[1]+color[2]+color[2]+color[3]+color[3];
        return color.length === 7 ? color.toLowerCase() : null;
      }
      const tmp = document.createElement('canvas');
      tmp.width = tmp.height = 1;
      const ctx = tmp.getContext('2d');
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, 1, 1);
      const d = ctx.getImageData(0, 0, 1, 1).data;
      return `#${d[0].toString(16).padStart(2,'0')}${d[1].toString(16).padStart(2,'0')}${d[2].toString(16).padStart(2,'0')}`;
    },

    // ================================================================
    // SVG SHIRT COLOUR
    // ================================================================
    resolveColourHex(colourName) {
      const key = (colourName || '').toLowerCase().trim();
      if (this.COLOUR_MAP[key]) return this.COLOUR_MAP[key];
      // Partial / substring match
      for (const [mapKey, hex] of Object.entries(this.COLOUR_MAP)) {
        if (key.includes(mapKey) || mapKey.includes(key)) return hex;
      }
      // Fall back to CSS colour parsing via a tiny canvas
      const tmp = document.createElement('canvas');
      tmp.width = tmp.height = 1;
      const ctx = tmp.getContext('2d');
      ctx.fillStyle = key.replace(/\s+/g, '');
      ctx.fillRect(0, 0, 1, 1);
      const d = ctx.getImageData(0, 0, 1, 1).data;
      if (d[0] === 0 && d[1] === 0 && d[2] === 0 && ctx.fillStyle !== '#000000') return '#cccccc';
      return `#${d[0].toString(16).padStart(2,'0')}${d[1].toString(16).padStart(2,'0')}${d[2].toString(16).padStart(2,'0')}`;
    },

    darkenHex(hex, amount) {
      const r = Math.max(0, parseInt(hex.slice(1,3), 16) - amount);
      const g = Math.max(0, parseInt(hex.slice(3,5), 16) - amount);
      const b = Math.max(0, parseInt(hex.slice(5,7), 16) - amount);
      return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
    },

    applyShirtColour(colourName) {
      const hex = this.resolveColourHex(colourName);
      this.shirtHex = hex;

      // Target shirt body elements inside any injected SVG
      document.querySelectorAll('.tsd-shirt-svg .tsd-shirt-body').forEach(el => {
        el.setAttribute('fill', hex);
      });

      // Update design text colours for contrast
      this.updateDesignColoursForShirt(colourName);
    },

    // ================================================================
    // COLOUR SWATCHES
    // ================================================================
    bindColourSwatches() {
      // Resolve hex for each swatch dot using COLOUR_MAP (Bug 5 — multi-word names)
      document.querySelectorAll('.tsd-colour-swatch').forEach(s => {
        const name = s.dataset.colour || '';
        if (name) {
          const inner = s.querySelector('.tsd-swatch-inner');
          if (inner) inner.style.backgroundColor = this.resolveColourHex(name);
        }
      });

      document.querySelectorAll('.tsd-colour-swatch').forEach(swatch => {
        swatch.addEventListener('click', () => {
          document.querySelectorAll('.tsd-colour-swatch').forEach(s => s.classList.remove('tsd-colour-swatch--active'));
          swatch.classList.add('tsd-colour-swatch--active');

          const colour    = swatch.dataset.colour;
          const variantId = swatch.dataset.variantId;
          const frontUrl  = swatch.dataset.imageFront || swatch.dataset.image || '';
          const backUrl   = swatch.dataset.imageBack  || '';

          this._currentColour = colour;
          const colourLabel = document.getElementById('tsd-selected-colour');
          if (colourLabel) colourLabel.textContent = colour;

          const variantInput = document.getElementById('tsd-variant-id');
          if (variantInput && variantId) variantInput.value = variantId;

          // Update price (colour + current print position) + size availability
          this._updateVariantPrice(colour, this.currentPosition);
          this._updateSizeAvailability(colour);

          // Swap mockup images then recolour the SVG if possible (Bug 1)
          this._swapVariantImages(frontUrl, backUrl, colour);
        });
      });
    },

    // Swap the mockup to a new variant's images and recolour the shirt
    _swapVariantImages(frontUrl, backUrl, colour) {
      const container = document.getElementById('tsd-mockup-container');
      if (!container) return;

      // Update stored URLs so view-toggle and back-side fetch use the right images
      if (frontUrl) container.dataset.frontUrl = frontUrl;
      if (backUrl)  container.dataset.backUrl  = backUrl;

      // Remove previously injected inline SVGs
      ['tsd-shirt-front', 'tsd-shirt-back'].forEach(id => {
        document.getElementById(id)?.remove();
      });

      // Reset to front view so the user always sees the newly selected colour
      this.currentView = 'front';
      document.querySelectorAll('.tsd-view-btn').forEach(b => {
        b.classList.toggle('tsd-view-btn--active', b.dataset.view === 'front');
      });
      this.syncViewToggleVisibility();

      // Show <img> fallback immediately while SVG re-injection attempts
      let img = document.getElementById('tsd-mockup-img');
      if (frontUrl) {
        if (!img) {
          img = document.createElement('img');
          img.id        = 'tsd-mockup-img';
          img.className = 'tsd-mockup-img';
          img.alt       = '';
          const printArea = document.getElementById('tsd-print-area');
          if (printArea) container.insertBefore(img, printArea);
          else           container.appendChild(img);
        }
        img.src = frontUrl;
        img.style.display = '';
        img.onload = () => {
          this.mockupNaturalW = img.naturalWidth  || 800;
          this.mockupNaturalH = img.naturalHeight || 800;
          this.resizeCanvas();
        };
      }

      // Try injecting front image as inline SVG (enables true colour repainting)
      this.fetchAndInjectSVG(frontUrl, 'tsd-shirt-front', false).then(svgEl => {
        if (svgEl) {
          // Hide the <img> fallback — the inline SVG takes over
          if (img) img.style.display = 'none';
        }
        // Always update shirtHex + data-shirt-dark whether SVG or JPEG
        this.applyShirtColour(colour);
        this.resizeCanvas();
      });

      // Pre-cache back SVG in background for when user switches to Back view
      if (backUrl) {
        this.fetchAndInjectSVG(backUrl, 'tsd-shirt-back', true).then(svgEl => {
          if (svgEl && this.shirtHex !== '#cccccc') {
            svgEl.querySelectorAll('.tsd-shirt-body')
              .forEach(el => el.setAttribute('fill', this.shirtHex));
          }
        });
      }

      this.resizeCanvas();
    },

    // Update the on-page price display when colour OR print position changes.
    // Price varies by print position (the priced variant axis), so the match
    // must honour both colour and the currently selected position.
    _updateVariantPrice(colour, position) {
      const variants  = window.tsdVariants || [];
      const colorIdx  = window.tsdColorIdx ?? -1;
      const posIdx    = window.tsdPosIdx ?? -1;
      colour   = colour   != null ? colour   : this._currentColour;
      position = position != null ? position : this.currentPosition;

      const colourOk = (v) => colorIdx < 0 || !colour   || v.options[colorIdx] === colour;
      const posOk    = (v) => posIdx   < 0 || !position || v.options[posIdx]   === position;

      // Prefer a variant matching colour + position; fall back to colour, then any.
      const match = variants.find(v => colourOk(v) && posOk(v))
                 || variants.find(v => colourOk(v))
                 || variants[0];
      if (!match || !match.price) return;

      // Format price in pence/cents → currency string
      const fmt = (cents) => {
        const money = window.tsdMoneyFormat || '£{{amount}}';
        const amount = (cents / 100).toFixed(2);
        return money.replace('{{amount}}', amount)
                    .replace('{{amount_no_decimals}}', Math.round(cents / 100))
                    .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
      };

      // Target the price item inside our price wrapper
      const priceWrap = document.getElementById(window.tsdPriceElId || '') || document.querySelector('.tsd-price-wrap');
      if (!priceWrap) return;

      // The price snippet renders two blocks toggled by `.price__hidden`:
      //   .price__regular  → .price                       (normal price)
      //   .price__sale     → .price-item--sale  (sale)    + .price-item--regular (compare)
      // Normally-priced products have NO .price-item--regular, so we must update
      // .price__regular .price directly (the previous selector silently no-op'd).
      const regularBlock = priceWrap.querySelector('.price__regular');
      const saleBlock    = priceWrap.querySelector('.price__sale');
      const regularPrice = regularBlock?.querySelector('.price') || priceWrap.querySelector('.price');
      const salePrice    = priceWrap.querySelector('.price-item--sale');
      const comparePrice = saleBlock?.querySelector('.price-item--regular');

      if (match.comparePrice && match.comparePrice > match.price) {
        // On sale
        if (salePrice)    salePrice.textContent    = fmt(match.price);
        if (comparePrice) comparePrice.textContent = fmt(match.comparePrice);
        regularBlock?.classList.add('price__hidden');
        saleBlock?.classList.remove('price__hidden');
      } else {
        // Regular
        if (regularPrice) regularPrice.textContent = fmt(match.price);
        regularBlock?.classList.remove('price__hidden');
        saleBlock?.classList.add('price__hidden');
      }
    },

    // Grey out / disable size rows not available for the selected colour
    _updateSizeAvailability(colour) {
      const variants = window.tsdVariants || [];
      const colorIdx = window.tsdColorIdx ?? -1;
      const sizeIdx  = window.tsdSizeIdx  ?? -1;
      if (sizeIdx < 0) return;

      document.querySelectorAll('.tsd-sqt-row').forEach(row => {
        const size = row.dataset.size;
        const available = variants.some(v => {
          const colorOk = colorIdx < 0 || v.options[colorIdx] === colour;
          const sizeOk  = sizeIdx  < 0 || v.options[sizeIdx]  === size;
          return colorOk && sizeOk && v.available;
        });

        row.classList.toggle('tsd-sqt-row--unavailable', !available);
        const input = row.querySelector('.tsd-sqt-input');
        const minus = row.querySelector('[data-action="minus"]');
        const plus  = row.querySelector('[data-action="plus"]');
        if (input) { input.disabled = !available; if (!available) input.value = 0; }
        if (minus) minus.disabled = !available;
        if (plus)  plus.disabled  = !available;
      });

      this.updateTotalQty();
    },

    // ================================================================
    // SHIRT COLOUR ADAPTATION
    // ================================================================
    updateDesignColoursForShirt(colourName) {
      const isDark = this.isColourDark(colourName);

      // Flip the text colour picker DEFAULT for the next text the user adds —
      // but only while they haven't manually chosen a colour. Once a font
      // colour is picked it must survive variant changes (the chosen colour
      // should never be overwritten/"bled" by the shirt colour).
      const textColourInput = document.getElementById('tsd-text-colour');
      const colourValueEl   = document.getElementById('tsd-colour-value');
      if (textColourInput && !this._userPickedTextColour) {
        const newColour = isDark ? '#ffffff' : '#000000';
        textColourInput.value = newColour;
        if (colourValueEl) colourValueEl.textContent = newColour.toUpperCase();
      }

      // Note: existing design objects (text + uploaded images) are deliberately
      // left untouched here — changing the shirt variant must not recolour the
      // customer's design.

      // Store on the section element — CSS uses [data-shirt-dark="1"] to switch blend mode
      const section = document.getElementById('tsd-canvas');
      if (section) section.closest('section')?.setAttribute('data-shirt-dark', isDark ? '1' : '0');
    },

    isColourDark(colourName) {
      const hex = this.resolveColourHex(colourName);
      const r = parseInt(hex.slice(1,3), 16);
      const g = parseInt(hex.slice(3,5), 16);
      const b = parseInt(hex.slice(5,7), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
    },

    // ================================================================
    // SIZE × QUANTITY TABLE
    // ================================================================
    bindSizeQtyTable() {
      document.querySelectorAll('.tsd-sqt-row').forEach(row => {
        const input = row.querySelector('.tsd-sqt-input');
        const minus = row.querySelector('[data-action="minus"]');
        const plus  = row.querySelector('[data-action="plus"]');
        if (!input) return;

        minus?.addEventListener('click', () => {
          const v = parseInt(input.value) || 0;
          if (v > 0) { input.value = v - 1; this.updateTotalQty(); }
        });
        plus?.addEventListener('click', () => {
          const v = parseInt(input.value) || 0;
          if (v < 999) { input.value = v + 1; this.updateTotalQty(); }
        });
        input.addEventListener('change', () => {
          let v = parseInt(input.value) || 0;
          if (v < 0)   v = 0;
          if (v > 999) v = 999;
          input.value = v;
          this.updateTotalQty();
        });
      });
    },

    updateTotalQty() {
      let total = 0;
      document.querySelectorAll('.tsd-sqt-input').forEach(inp => {
        total += parseInt(inp.value) || 0;
      });
      const el = document.getElementById('tsd-total-qty');
      if (el) el.textContent = total;
    },

    // ================================================================
    // FORM SUBMIT — multi-size JSON cart add
    // ================================================================
    bindFormSubmit() {
      const form = document.querySelector('.tsd-product-form');
      if (!form) return;

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const btn    = document.getElementById('tsd-atc-btn');
        const textEl = document.getElementById('tsd-atc-text');

        const setLoading = (on) => {
          if (!btn) return;
          btn.disabled = on;
          btn.classList.toggle('loading', on);
          if (textEl) textEl.style.visibility = on ? 'hidden' : '';
        };

        // --- Guard: require at least something on the canvas ---
        const hasDesign = Object.values(this.designs).some(d => d && d.objects && d.objects.length > 0)
                       || this.canvas.getObjects().length > 0;
        if (!hasDesign) {
          const ok = await this.tsdConfirm('You haven’t added any text or images to your design yet. Add it to the cart anyway?', {
            title: 'No design added', icon: 'question', okLabel: 'Add to cart', cancelLabel: 'Keep designing',
          });
          if (!ok) return;
        }

        // --- Collect size-qty rows ---
        const sqtRows  = document.querySelectorAll('.tsd-sqt-row');
        const variants = window.tsdVariants || [];
        const colorIdx = window.tsdColorIdx ?? -1;
        const sizeIdx  = window.tsdSizeIdx  ?? -1;
        const posIdx   = window.tsdPosIdx   ?? -1;

        const colourEl       = document.getElementById('tsd-selected-colour');
        const selectedColour = colourEl ? colourEl.textContent.trim() : null;
        const selectedPos    = this.currentPosition;

        const lineItems   = [];
        const outOfStock  = [];

        if (sqtRows.length > 0) {
          sqtRows.forEach(row => {
            const size = row.dataset.size;
            const qty  = parseInt(row.querySelector('.tsd-sqt-input')?.value) || 0;
            if (qty <= 0) return;

            const variant = variants.find(v => {
              const sizeOk  = sizeIdx  < 0 || v.options[sizeIdx]  === size;
              const colorOk = colorIdx < 0 || !selectedColour || v.options[colorIdx] === selectedColour;
              const posOk   = posIdx   < 0 || !selectedPos     || v.options[posIdx]   === selectedPos;
              return sizeOk && colorOk && posOk;
            });

            if (!variant) return;
            if (!variant.available) { outOfStock.push(size); return; }
            lineItems.push({ variantId: variant.id, quantity: qty, size });
          });
        } else {
          // No size option — match by colour + position, else hidden variant id
          const variantId = parseInt(document.getElementById('tsd-variant-id')?.value);
          const variant   = variants.find(v => {
                              const colorOk = colorIdx < 0 || !selectedColour || v.options[colorIdx] === selectedColour;
                              const posOk   = posIdx   < 0 || !selectedPos     || v.options[posIdx]   === selectedPos;
                              return (colorIdx >= 0 || posIdx >= 0) && colorOk && posOk;
                            })
                         || variants.find(v => v.id === variantId);
          if (variant && !variant.available) {
            this.tsdAlert('This item is currently out of stock.', { title: 'Out of stock', icon: 'warning' });
            return;
          }
          if (variantId) lineItems.push({ variantId, quantity: 1, size: null });
        }

        if (outOfStock.length > 0) {
          this.tsdToast('Removed out-of-stock sizes: ' + outOfStock.join(', '), { type: 'warning' });
        }

        if (lineItems.length === 0) {
          this.tsdAlert('Please enter a quantity for at least one size.', { title: 'Choose a quantity', icon: 'info' });
          return;
        }

        setLoading(true);

        try {
          // 1. Serialize design data into hidden fields
          this.serializeDesigns();

          // 2. Capture canvas preview thumbnails (JPEG, stored as line item properties
          //    so the print team can see the design in the Shopify order)
          await this.capturePreviewAsync();

          // 3. Build shared order properties — human-readable + preview images.
          //    _design_front/_design_back (raw Fabric JSON) are intentionally excluded:
          //    they embed full base64 image data and are too large for reliable storage.
          const sharedProps = {};
          const propMap = {
            '_preview_front':  document.getElementById('tsd-preview-front')?.value,
            '_preview_back':   document.getElementById('tsd-preview-back')?.value,
            'Colour':          document.getElementById('tsd-prop-colour')?.value,
            'Print Position':  document.getElementById('tsd-prop-position')?.value,
            'Print Sides':     document.getElementById('tsd-prop-sides')?.value,
            'Custom Text':     document.getElementById('tsd-prop-text')?.value,
            'Design Elements': document.getElementById('tsd-prop-elements')?.value,
          };
          Object.entries(propMap).forEach(([k, v]) => { if (v) sharedProps[k] = v; });

          // Original full-resolution artwork URLs for the print team. Visible
          // (no underscore prefix) so they show on the order in Shopify admin.
          // If any upload failed we stop here — better a retryable error than a
          // paid order the print team can't fulfil.
          const artwork = await this.collectArtworkProps();
          if (artwork.missing > 0) {
            setLoading(false);
            this.tsdAlert(
              'We couldn’t save your uploaded image to our print system. Please check your connection and try again — your design is safe and still on screen.',
              { title: 'Image not saved', icon: 'error' }
            );
            return;
          }
          Object.assign(sharedProps, artwork.props);

          // 4. Build items array — each size gets its own line item with Size property
          const items = lineItems.map(({ variantId, quantity, size }) => ({
            id: variantId,
            quantity,
            properties: { ...sharedProps, ...(size ? { Size: size } : {}) },
          }));

          // 5. POST items to cart — include sections so response has updated cart HTML
          const root = window.Shopify?.routes?.root || '/';
          const res  = await fetch(root + 'cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body:    JSON.stringify({
              items,
              sections: ['cart-drawer', 'cart-notification-button', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });

          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || 'Could not add to cart');
          }

          const cartData = await res.json();

          // 6. Update cart drawer + bubble using sections from response
          await this.refreshCart(cartData.sections);

          // 8. Flash "Added!" and reset quantities
          setLoading(false);
          if (textEl) {
            const original = textEl.textContent;
            textEl.textContent = 'Added!';
            textEl.style.visibility = '';
            document.querySelectorAll('.tsd-sqt-input').forEach(inp => { inp.value = 0; });
            this.updateTotalQty();
            setTimeout(() => { textEl.textContent = original; }, 2500);
          }

        } catch (err) {
          setLoading(false);
          this.tsdAlert(err.message || 'Something went wrong. Please try again.', { title: 'Couldn’t add to cart', icon: 'error' });
        }
      });
    },

    // ================================================================
    // BUY NOW — same flow as add-to-cart but redirects to checkout
    // ================================================================
    bindBuyNow() {
      const btn    = document.getElementById('tsd-buynow-btn');
      const textEl = document.getElementById('tsd-buynow-text');
      if (!btn) return;

      btn.addEventListener('click', async () => {
        const setLoading = (on) => {
          btn.disabled = on;
          btn.classList.toggle('loading', on);
          if (textEl) textEl.style.visibility = on ? 'hidden' : '';
        };

        const hasDesign = Object.values(this.designs).some(d => d && d.objects && d.objects.length > 0)
                       || this.canvas.getObjects().length > 0;
        if (!hasDesign) {
          const ok = await this.tsdConfirm('You haven’t added any text or images to your design yet. Continue to checkout anyway?', {
            title: 'No design added', icon: 'question', okLabel: 'Continue to checkout', cancelLabel: 'Keep designing',
          });
          if (!ok) return;
        }

        const sqtRows        = document.querySelectorAll('.tsd-sqt-row');
        const variants       = window.tsdVariants || [];
        const colorIdx       = window.tsdColorIdx ?? -1;
        const sizeIdx        = window.tsdSizeIdx  ?? -1;
        const posIdx         = window.tsdPosIdx   ?? -1;
        const colourEl       = document.getElementById('tsd-selected-colour');
        const selectedColour = colourEl ? colourEl.textContent.trim() : null;
        const selectedPos    = this.currentPosition;
        const lineItems      = [];
        const outOfStock     = [];

        if (sqtRows.length > 0) {
          sqtRows.forEach(row => {
            const size = row.dataset.size;
            const qty  = parseInt(row.querySelector('.tsd-sqt-input')?.value) || 0;
            if (qty <= 0) return;
            const variant = variants.find(v => {
              const sizeOk  = sizeIdx  < 0 || v.options[sizeIdx]  === size;
              const colorOk = colorIdx < 0 || !selectedColour || v.options[colorIdx] === selectedColour;
              const posOk   = posIdx   < 0 || !selectedPos     || v.options[posIdx]   === selectedPos;
              return sizeOk && colorOk && posOk;
            });
            if (!variant) return;
            if (!variant.available) { outOfStock.push(size); return; }
            lineItems.push({ variantId: variant.id, quantity: qty, size });
          });
        } else {
          const variantId = parseInt(document.getElementById('tsd-variant-id')?.value);
          const variant   = variants.find(v => {
                              const colorOk = colorIdx < 0 || !selectedColour || v.options[colorIdx] === selectedColour;
                              const posOk   = posIdx   < 0 || !selectedPos     || v.options[posIdx]   === selectedPos;
                              return (colorIdx >= 0 || posIdx >= 0) && colorOk && posOk;
                            })
                         || variants.find(v => v.id === variantId);
          if (variant && !variant.available) { this.tsdAlert('This item is currently out of stock.', { title: 'Out of stock', icon: 'warning' }); return; }
          if (variant) lineItems.push({ variantId: variant.id, quantity: 1, size: null });
        }

        if (outOfStock.length > 0) {
          this.tsdToast('Removed out-of-stock sizes: ' + outOfStock.join(', '), { type: 'warning' });
        }
        if (lineItems.length === 0) {
          this.tsdAlert('Please enter a quantity for at least one size.', { title: 'Choose a quantity', icon: 'info' });
          return;
        }

        setLoading(true);

        try {
          this.serializeDesigns();
          await this.capturePreviewAsync();

          const sharedProps = {};
          const propMap = {
            '_preview_front':  document.getElementById('tsd-preview-front')?.value,
            '_preview_back':   document.getElementById('tsd-preview-back')?.value,
            'Colour':          document.getElementById('tsd-prop-colour')?.value,
            'Print Position':  document.getElementById('tsd-prop-position')?.value,
            'Print Sides':     document.getElementById('tsd-prop-sides')?.value,
            'Custom Text':     document.getElementById('tsd-prop-text')?.value,
            'Design Elements': document.getElementById('tsd-prop-elements')?.value,
          };
          Object.entries(propMap).forEach(([k, v]) => { if (v) sharedProps[k] = v; });

          // Original full-resolution artwork URLs for the print team. Visible
          // (no underscore prefix) so they show on the order in Shopify admin.
          // If any upload failed we stop here — better a retryable error than a
          // paid order the print team can't fulfil.
          const artwork = await this.collectArtworkProps();
          if (artwork.missing > 0) {
            setLoading(false);
            this.tsdAlert(
              'We couldn’t save your uploaded image to our print system. Please check your connection and try again — your design is safe and still on screen.',
              { title: 'Image not saved', icon: 'error' }
            );
            return;
          }
          Object.assign(sharedProps, artwork.props);

          const items = lineItems.map(({ variantId, quantity, size }) => ({
            id: variantId,
            quantity,
            properties: { ...sharedProps, ...(size ? { Size: size } : {}) },
          }));

          const root = window.Shopify?.routes?.root || '/';
          const res  = await fetch(root + 'cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body:    JSON.stringify({ items }),
          });

          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || 'Could not add to cart');
          }

          // Redirect to checkout with all design data attached as line item properties
          window.location.href = root + 'checkout';

        } catch (err) {
          setLoading(false);
          this.tsdAlert(err.message || 'Something went wrong. Please try again.', { title: 'Checkout failed', icon: 'error' });
        }
      });
    },

    async refreshCart(sections) {
      // ── Update header bubble count ────────────────────────────────────
      if (sections && sections['cart-icon-bubble']) {
        const bubbleEl = document.getElementById('cart-icon-bubble');
        if (bubbleEl) {
          const p = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          const s = p.querySelector('.shopify-section');
          if (s) bubbleEl.innerHTML = s.innerHTML;
        }
      }

      // ── Cart Drawer ───────────────────────────────────────────────────
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        if (sections && sections['cart-drawer']) {
          const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          ['cart-drawer-items', '.cart-drawer__footer'].forEach(sel => {
            const src  = parsed.querySelector(sel);
            const dest = cartDrawer.querySelector(sel);
            if (src && dest) dest.replaceWith(src);
          });
          cartDrawer.classList.remove('is-empty');
          const di = cartDrawer.querySelector('.drawer__inner');
          if (di) di.classList.remove('is-empty');
        }
        cartDrawer.open();
        return;
      }

      // ── Cart Notification ─────────────────────────────────────────────
      const cartNotif = document.querySelector('cart-notification');
      if (cartNotif && typeof cartNotif.open === 'function') {
        cartNotif.open();
        return;
      }

      // ── Fallback: redirect to cart page ───────────────────────────────
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    },

    serializeDesigns() {
      this.saveCurrentView();

      const set = (id, val) => { const el = document.getElementById(id); if (el) el.value = val; };

      set('tsd-design-front', this.designs.front ? JSON.stringify(this.designs.front) : '');
      set('tsd-design-back',  this.designs.back  ? JSON.stringify(this.designs.back)  : '');

      const colourEl = document.getElementById('tsd-selected-colour');
      set('tsd-prop-colour', colourEl ? colourEl.textContent.trim() : '');

      const loc = this.PRINT_LOCATIONS[this.currentLocation];
      // Prefer the raw Print-Position variant value so the recorded property
      // matches the variant (and price) actually being charged.
      const posLabel = this.isHeadwear()
        ? 'Centre Front'
        : (this.currentPosition || (loc ? loc.label : 'Front Only'));
      set('tsd-prop-position', posLabel);

      const sides = [];
      if (this.designs.front?.objects?.length && loc?.sides.includes('front')) sides.push('Front');
      if (this.designs.back?.objects?.length  && loc?.sides.includes('back'))  sides.push('Back');
      set('tsd-prop-sides', sides.join(' + ') || 'No design');

      const allObjs = [
        ...(this.designs.front?.objects || []),
        ...(this.designs.back?.objects  || []),
      ];
      const texts = allObjs
        .filter(o => o.type === 'i-text' || o.type === 'text')
        .map(o => o.text?.replace(/\n/g, ' ').trim())
        .filter(Boolean);
      set('tsd-prop-text', texts.join(' | ') || '');

      const summary = (objs, label) => {
        // Uploaded SVGs are groups, not images — count them as artwork too.
        const imgs = objs.filter(o => o.type === 'image' || o._uploadDataUrl).length;
        const txts = objs.filter(o => o.type === 'i-text' || o.type === 'text').length;
        const parts = [];
        if (imgs) parts.push(`${imgs} image${imgs > 1 ? 's' : ''}`);
        if (txts) parts.push(`${txts} text layer${txts > 1 ? 's' : ''}`);
        return parts.length ? `${label}: ${parts.join(', ')}` : '';
      };
      const els = [
        summary(this.designs.front?.objects || [], 'Front'),
        summary(this.designs.back?.objects  || [], 'Back'),
      ].filter(Boolean).join(' | ');
      set('tsd-prop-elements', els || 'No design added');
    },

    capturePreviewAsync() {
      const loc     = this.PRINT_LOCATIONS[this.currentLocation] || {};
      const canvasW = this.canvas.width;
      const canvasH = this.canvas.height;

      // Guard: canvas not yet sized — skip preview rather than create a 0×0 Fabric canvas
      if (!canvasW || !canvasH) return Promise.resolve();

      // Returns a blob URL for the garment SVG at the given view, or null
      const garmentBlobUrl = (view) => {
        const svgEl = document.getElementById(view === 'front' ? 'tsd-shirt-front' : 'tsd-shirt-back');
        if (!svgEl) return null;
        const clone = svgEl.cloneNode(true);
        clone.style.display = 'block'; // override any display:none
        const svgStr = new XMLSerializer().serializeToString(clone);
        const blob   = new Blob([svgStr], { type: 'image/svg+xml' });
        return URL.createObjectURL(blob);
      };

      const renderComposite = (view) => new Promise(async (resolve) => {
        const saved     = this.designs[view];
        const hasDesign = saved && saved.objects && saved.objects.length > 0;

        // Only upload a preview if the customer actually put a design on this side
        if (!hasDesign) { resolve(''); return; }

        const blobUrl = garmentBlobUrl(view);

        // Get output dimensions from the SVG viewBox of the front garment (use front as reference)
        let outW = 800, outH = 900;
        const frontSvg = document.getElementById('tsd-shirt-front');
        if (frontSvg) {
          const vb = frontSvg.viewBox?.baseVal;
          if (vb && vb.width > 0 && vb.height > 0) { outW = vb.width; outH = vb.height; }
        }
        // Cap to 1200px wide — prevents browser canvas limits and imgBB 32MB size cap
        if (outW > 1200) { outH = Math.round(outH * 1200 / outW); outW = 1200; }

        // Create output 2D canvas
        const out = document.createElement('canvas');
        out.width  = outW;
        out.height = outH;
        const ctx  = out.getContext('2d');

        // Step 1: draw garment as background
        if (blobUrl) {
          await new Promise((res) => {
            const img = new Image();
            img.onload  = () => { ctx.drawImage(img, 0, 0, outW, outH); URL.revokeObjectURL(blobUrl); res(); };
            img.onerror = () => { URL.revokeObjectURL(blobUrl); res(); };
            img.src = blobUrl;
          });
        } else {
          // Raster fallback: try the visible mockup img
          const imgEl = document.getElementById('tsd-mockup-img');
          if (imgEl && imgEl.src) {
            await new Promise((res) => {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload  = () => { ctx.drawImage(img, 0, 0, outW, outH); res(); };
              img.onerror = () => res();
              img.src = imgEl.src;
            });
          }
        }

        // Step 2: draw design layer at the correct print area position
        const area      = loc.areas?.[view] || { topPct: 0.19, leftPct: 0.28, widthPct: 0.44, heightPct: 0.52 };
        const printLeft = Math.round(outW * area.leftPct);
        const printTop  = Math.round(outH * area.topPct);
        const printW    = Math.round(outW * area.widthPct);
        const printH    = Math.round(outH * area.heightPct);

        await new Promise((res) => {
          const wrapper = document.createElement('div');
          wrapper.setAttribute('aria-hidden', 'true');
          wrapper.style.cssText = 'position:fixed;left:-9999px;top:-9999px;pointer-events:none;visibility:hidden;';
          const el = document.createElement('canvas');
          wrapper.appendChild(el);
          document.body.appendChild(wrapper);

          const tmp = new fabric.Canvas(el, {
            width: canvasW, height: canvasH,
            enableRetinaScaling: false, renderOnAddRemove: false,
          });

          tmp.loadFromJSON(saved, () => {
            this.reapplyPremiumFonts(tmp);
            tmp.renderAll();
            requestAnimationFrame(() => requestAnimationFrame(() => {
              // drawImage scales the design canvas to fit the print area rect
              ctx.drawImage(tmp.lowerCanvasEl, printLeft, printTop, printW, printH);
              tmp.dispose();
              document.body.removeChild(wrapper);
              res();
            }));
          });
        });

        // Step 3: export composite and upload to imgBB
        let b64 = '';
        try { b64 = out.toDataURL('image/jpeg', 0.8); } catch (_) { /* tainted */ }

        if (!b64) { resolve(''); return; }

        try {
          const base64Data = b64.replace(/^data:image\/\w+;base64,/, '');
          const body       = new FormData();
          body.append('key', IMGBB_KEY);
          body.append('image', base64Data);
          const controller = new AbortController();
          const timer      = setTimeout(() => controller.abort(), 8000);
          const res        = await fetch('https://api.imgbb.com/1/upload', { method: 'POST', body, signal: controller.signal });
          clearTimeout(timer);
          const json = await res.json();
          resolve(json?.data?.display_url || json?.data?.url || '');
        } catch (_) {
          resolve(''); // timeout, network error, or bad response — skip silently
        }
      });

      return Promise.all([renderComposite('front'), renderComposite('back')]).then(([f, b]) => {
        const set = (id, val) => { const el = document.getElementById(id); if (el) el.value = val; };
        set('tsd-preview-front', f);
        set('tsd-preview-back',  b);
      });
    },

    // ================================================================
    // ACCORDIONS
    // ================================================================
    bindAccordions() {
      document.querySelectorAll('.tsd-accordion-toggle').forEach(toggle => {
        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          const body     = toggle.nextElementSibling;

          toggle.setAttribute('aria-expanded', String(!expanded));

          if (body) {
            if (expanded) {
              body.style.maxHeight = body.scrollHeight + 'px';
              requestAnimationFrame(() => {
                body.style.maxHeight = '0';
                body.style.overflow  = 'hidden';
                body.style.transition = 'max-height 0.25s ease';
              });
              setTimeout(() => {
                body.hidden = true;
                body.style.maxHeight = '';
                body.style.overflow  = '';
              }, 260);
            } else {
              body.hidden = false;
              body.style.maxHeight = '0';
              body.style.overflow  = 'hidden';
              body.style.transition = 'max-height 0.25s ease';
              requestAnimationFrame(() => {
                body.style.maxHeight = body.scrollHeight + 'px';
              });
              setTimeout(() => {
                body.style.maxHeight = '';
                body.style.overflow  = '';
              }, 260);
            }
          }
        });
      });
    },

    // ================================================================
    // MOBILE BOTTOM-SHEET
    // ================================================================
    bindMobileSheet() {
      const panel    = document.getElementById('tsd-left-panel');
      const backdrop = document.getElementById('tsd-sheet-backdrop');
      const openBtn  = document.getElementById('tsd-mobile-edit-btn');
      const closeBtn = document.getElementById('tsd-sheet-close');
      if (!panel || !backdrop) return;

      const open = () => {
        panel.classList.add('tsd-sheet-open');
        backdrop.classList.add('tsd-sheet-open');
        document.body.style.overflow = 'hidden';
      };
      const close = () => {
        panel.classList.remove('tsd-sheet-open');
        backdrop.classList.remove('tsd-sheet-open');
        document.body.style.overflow = '';
      };

      openBtn?.addEventListener('click', open);
      closeBtn?.addEventListener('click', close);
      backdrop.addEventListener('click', close);

      // Close sheet when viewport grows to desktop size
      window.addEventListener('resize', () => {
        if (window.innerWidth > 899) close();
      });
    },

    // ================================================================
    // RESPONSIVE COLOUR PICKER
    // On mobile the colour swatches live inside the "Edit Design" sheet
    // (next to the design tools) so the customer can pick a colour while
    // editing. On desktop they stay in the right-hand product column.
    //
    // The SAME #tsd-color-group node is physically moved between the two
    // locations — never duplicated — so there is exactly one source of
    // truth (#tsd-selected-colour + .tsd-colour-swatch). The cart/checkout
    // code reads those by id/class, so the selected colour is preserved
    // through to checkout regardless of where the picker is shown.
    // ================================================================
    relocateColourGroup() {
      const group = document.getElementById('tsd-color-group');
      if (!group) return;

      const designBox = document.querySelector('#tsd-left-panel .tsd-design-box');
      if (!designBox) return;

      // Remember the original desktop position with a stable anchor node so
      // we can put the picker back exactly where it was.
      if (!this._colourHomeAnchor) {
        this._colourHomeAnchor = document.createComment('tsd-colour-home');
        group.parentNode.insertBefore(this._colourHomeAnchor, group);
      }

      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      if (isMobile) {
        // Place just below the Print Location picker, near the top of the sheet.
        const after = designBox.querySelector('.tsd-location-picker') || designBox.firstElementChild;
        if (group.parentNode !== designBox || (after && group.previousElementSibling !== after)) {
          if (after && after.parentNode === designBox) {
            designBox.insertBefore(group, after.nextSibling);
          } else {
            designBox.appendChild(group);
          }
        }
        group.classList.add('tsd-color-group--in-sheet');
      } else {
        // Restore to the right-hand column at its original spot.
        const anchor = this._colourHomeAnchor;
        if (group.previousSibling !== anchor) {
          anchor.parentNode.insertBefore(group, anchor.nextSibling);
        }
        group.classList.remove('tsd-color-group--in-sheet');
      }
    },

    // ================================================================
    // ZOOM
    // ================================================================
    bindZoom() {
      const zoomIn   = document.getElementById('tsd-zoom-in');
      const zoomOut  = document.getElementById('tsd-zoom-out');
      const zoomLvl  = document.getElementById('tsd-zoom-level');
      const printArea = document.getElementById('tsd-print-area');

      if (!zoomIn || !zoomOut || !printArea) return;

      const updateZoom = () => {
        printArea.style.transform = `scale(${this.zoomLevel})`;
        printArea.style.transformOrigin = 'top left';
        if (zoomLvl) zoomLvl.textContent = Math.round(this.zoomLevel * 100) + '%';
      };

      zoomIn.addEventListener('click', () => {
        if (this.zoomLevel < 2) {
          this.zoomLevel = Math.min(2, this.zoomLevel + 0.1);
          updateZoom();
        }
      });

      zoomOut.addEventListener('click', () => {
        if (this.zoomLevel > 0.5) {
          this.zoomLevel = Math.max(0.5, this.zoomLevel - 0.1);
          updateZoom();
        }
      });
    },

    // ================================================================
    // KEYBOARD SHORTCUTS
    // ================================================================
    bindKeyboard() {
      document.addEventListener('keydown', (e) => {
        // Undo / Redo — work even when input is focused (except native text undo in inputs)
        if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
          const tag = document.activeElement.tagName;
          if (tag !== 'INPUT' && tag !== 'TEXTAREA') { e.preventDefault(); this.undo(); }
          return;
        }
        if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
          e.preventDefault(); this.redo(); return;
        }

        const tag = document.activeElement.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

        const active = this.canvas.getActiveObject();

        if ((e.key === 'Delete' || e.key === 'Backspace') && active) {
          e.preventDefault();
          this.canvas.remove(active);
          this.canvas.discardActiveObject();
          this.canvas.renderAll();
          this.onSelectionChange();
          return;
        }

        // Nudge with arrow keys
        if (active && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
          e.preventDefault();
          const step = e.shiftKey ? 10 : 1;
          switch (e.key) {
            case 'ArrowUp':    active.set('top',  active.top  - step); break;
            case 'ArrowDown':  active.set('top',  active.top  + step); break;
            case 'ArrowLeft':  active.set('left', active.left - step); break;
            case 'ArrowRight': active.set('left', active.left + step); break;
          }
          active.setCoords();
          this.canvas.renderAll();
          this.updateInspector();
        }

        // Escape → deselect
        if (e.key === 'Escape') {
          this.canvas.discardActiveObject();
          this.canvas.renderAll();
          this.onSelectionChange();
        }
      });
    },

    // ================================================================
    // UNDO / REDO
    // ================================================================
    _debouncePushHistory() {
      if (this._historyPaused) return;
      clearTimeout(this._historyTimer);
      this._historyTimer = setTimeout(() => this._pushHistory(), 350);
    },

    _pushHistory() {
      if (this._historyPaused) return;
      const json = this.canvas.toJSON(['id', 'selectable', '_uploadDataUrl', '_premium', '_premiumKey', '_premiumText', '_premiumFontSize', '_premiumEmPx']);
      json._canvasW = this.canvas.width;
      json._canvasH = this.canvas.height;
      const snapshot = {
        front: this.currentView === 'front' ? json : (this.designs.front ? JSON.parse(JSON.stringify(this.designs.front)) : null),
        back:  this.currentView === 'back'  ? json : (this.designs.back  ? JSON.parse(JSON.stringify(this.designs.back))  : null),
      };
      // Discard forward history (branching on new action)
      this._historyStack.splice(this._historyIndex + 1);
      this._historyStack.push(snapshot);
      if (this._historyStack.length > 30) this._historyStack.shift();
      this._historyIndex = this._historyStack.length - 1;
      this._updateUndoRedoBtns();
      this._debounceSaveStorage();
    },

    undo() {
      if (this._historyIndex <= 0) return;
      // Sync current canvas state into the current history slot before stepping back
      const json = this.canvas.toJSON(['id', 'selectable', '_uploadDataUrl', '_premium', '_premiumKey', '_premiumText', '_premiumFontSize', '_premiumEmPx']);
      json._canvasW = this.canvas.width;
      json._canvasH = this.canvas.height;
      if (this._historyStack[this._historyIndex]) {
        this._historyStack[this._historyIndex][this.currentView] = json;
      }
      this._historyIndex--;
      this._applyHistorySnapshot(this._historyStack[this._historyIndex]);
      this._updateUndoRedoBtns();
    },

    redo() {
      if (this._historyIndex >= this._historyStack.length - 1) return;
      this._historyIndex++;
      this._applyHistorySnapshot(this._historyStack[this._historyIndex]);
      this._updateUndoRedoBtns();
    },

    _applyHistorySnapshot(snapshot) {
      if (!snapshot) return;
      this._historyPaused = true;
      this.designs.front = snapshot.front;
      this.designs.back  = snapshot.back;
      const viewData = snapshot[this.currentView];
      if (viewData && viewData.objects) {
        this.canvas.loadFromJSON(viewData, () => {
          const savedW = viewData._canvasW;
          const savedH = viewData._canvasH;
          const cx = this.canvas.width;
          const cy = this.canvas.height;
          if (savedW && savedH && (Math.abs(cx / savedW - 1) > 0.01 || Math.abs(cy / savedH - 1) > 0.01)) {
            const s = Math.min(cx / savedW, cy / savedH);
            this.canvas.getObjects().forEach(obj => {
              obj.set({ left: (obj.left / savedW) * cx, top: (obj.top / savedH) * cy, scaleX: obj.scaleX * s, scaleY: obj.scaleY * s });
              obj.setCoords();
            });
          }
          this.reapplyPremiumFonts();
          this.canvas.renderAll();
          this.onSelectionChange();
          this._historyPaused = false;
        });
      } else {
        this.canvas.clear();
        this.canvas.renderAll();
        this.onSelectionChange();
        this._historyPaused = false;
      }
    },

    _updateUndoRedoBtns() {
      const u = document.getElementById('tsd-btn-undo');
      const r = document.getElementById('tsd-btn-redo');
      if (u) u.disabled = this._historyIndex <= 0;
      if (r) r.disabled = this._historyIndex >= this._historyStack.length - 1;
    },

    bindUndoRedo() {
      document.getElementById('tsd-btn-undo')?.addEventListener('click', () => this.undo());
      document.getElementById('tsd-btn-redo')?.addEventListener('click', () => this.redo());
      this._updateUndoRedoBtns();
    },

    // ================================================================
    // LOCALSTORAGE — auto-save & restore
    // ================================================================
    _debounceSaveStorage() {
      clearTimeout(this._storageTimer);
      this._storageTimer = setTimeout(() => this._saveToStorage(), 1200);
    },

    _saveToStorage() {
      try {
        const key = 'tsd_' + (window.tsdProductHandle || location.pathname);
        const data = {
          front:    this.designs.front,
          back:     this.designs.back,
          location: this.currentLocation,
          colour:   document.getElementById('tsd-selected-colour')?.textContent.trim() || '',
          ts:       Date.now(),
        };
        localStorage.setItem(key, JSON.stringify(data));
      } catch (_) {}
    },

    initLocalStorage() {
      try {
        const key = 'tsd_' + (window.tsdProductHandle || location.pathname);
        const raw = localStorage.getItem(key);
        if (!raw) return;
        const data = JSON.parse(raw);
        const hasContent = (data.front?.objects?.length > 0) || (data.back?.objects?.length > 0);
        const fresh = data.ts && (Date.now() - data.ts) < 86400000; // 24h
        if (!hasContent || !fresh) { localStorage.removeItem(key); return; }
        this._showRestoreBanner(data, key);
      } catch (_) {}
    },

    _showRestoreBanner(data, key) {
      const banner = document.createElement('div');
      banner.id = 'tsd-restore-banner';
      banner.innerHTML = `
        <span style="flex:1">You have a saved design from your last visit.</span>
        <button id="tsd-restore-yes" type="button" style="padding:0.35rem 0.85rem;background:#fff;color:#000;border:none;font-weight:700;cursor:pointer;font-size:0.8125rem;">Restore</button>
        <button id="tsd-restore-no"  type="button" style="padding:0.35rem 0.85rem;background:transparent;color:rgba(255,255,255,0.7);border:1px solid rgba(255,255,255,0.3);cursor:pointer;font-size:0.8125rem;">Dismiss</button>
      `;
      Object.assign(banner.style, {
        position: 'fixed', bottom: '1.25rem', left: '50%', transform: 'translateX(-50%)',
        background: '#111', color: '#fff', padding: '0.65rem 1rem', display: 'flex',
        alignItems: 'center', gap: '0.75rem', zIndex: '9999',
        fontFamily: 'sans-serif', fontSize: '0.8125rem',
        boxShadow: '0 4px 20px rgba(0,0,0,0.35)', whiteSpace: 'nowrap',
        maxWidth: 'calc(100vw - 2rem)',
      });
      document.body.appendChild(banner);

      document.getElementById('tsd-restore-yes')?.addEventListener('click', () => {
        this._historyPaused = true;
        this.designs.front = data.front || null;
        this.designs.back  = data.back  || null;
        this.loadView(this.currentView);
        setTimeout(() => { this._historyPaused = false; this._pushHistory(); }, 300);
        banner.remove();
      });
      document.getElementById('tsd-restore-no')?.addEventListener('click', () => {
        localStorage.removeItem(key);
        banner.remove();
      });
      setTimeout(() => banner.isConnected && banner.remove(), 12000);
    },

    // ================================================================
    // ARC TEXT
    // ================================================================
    addArcTextToCanvas() {
      const textInput = document.getElementById('tsd-text-input');
      // Arc text bends one run of characters around a circle — it has no second
      // line to bend, so collapse any line breaks into spaces.
      const text = textInput ? textInput.value.replace(/\s*\n\s*/g, ' ').trim() : '';
      if (!text) { textInput?.focus(); return; }

      const fontFamily = document.getElementById('tsd-font-family')?.value || 'Arial';
      const fontSize   = Math.max(8, Math.min(120, parseInt(document.getElementById('tsd-font-size')?.value) || 40));
      const fill       = document.getElementById('tsd-text-colour')?.value || '#000000';
      const fontWeight = document.getElementById('tsd-bold')?.classList.contains('tsd-style-btn--active') ? 'bold' : 'normal';

      // Slider 20–180 → map to actual pixel radius relative to canvas width
      const sliderVal = parseInt(document.getElementById('tsd-arc-radius')?.value) || 100;
      const radius    = (sliderVal / 180) * this.canvas.width * 0.9;

      const chars = text.split('');
      if (chars.length === 0) return;

      // Estimate total arc angle: each char occupies ~0.6 × fontSize of arc length
      const charArc    = (fontSize * 0.6) / radius; // radians per char
      const totalAngle = charArc * chars.length;
      const startAngle = -Math.PI / 2 - totalAngle / 2;

      const objects = chars.map((char, i) => {
        const angle  = startAngle + (i + 0.5) * charArc;
        const x      = radius * Math.cos(angle);
        const y      = radius * Math.sin(angle);
        const rotDeg = (angle + Math.PI / 2) * (180 / Math.PI);
        return new fabric.Text(char, {
          left: x, top: y,
          originX: 'center', originY: 'center',
          fontFamily, fontSize, fill, fontWeight,
          angle: rotDeg,
        });
      });

      const group = new fabric.Group(objects, {
        left: this.canvas.width  / 2,
        top:  this.canvas.height / 2,
        originX: 'center',
        originY: 'center',
      });
      group.id = 'arc-' + Date.now();

      this.canvas.add(group);
      this.canvas.setActiveObject(group);
      this.canvas.renderAll();
      this.onSelectionChange();
      // Leave the text in the box (selected) — same as straight text.
      this._textCommitted = true;
      if (textInput) {
        textInput.focus();
        textInput.select();
      }
    },

    // ================================================================
    // GOOGLE FONTS
    // ================================================================
    loadGoogleFonts() {
      if (document.getElementById('tsd-gfonts')) return;
      const families = [
        'Bebas+Neue', 'Chewy', 'Fondamento',
        'Permanent+Marker', 'Russo+One', 'Ultra',
      ];
      const pre = document.createElement('link');
      pre.rel = 'preconnect'; pre.href = 'https://fonts.googleapis.com';
      document.head.appendChild(pre);
      const pre2 = document.createElement('link');
      pre2.rel = 'preconnect'; pre2.href = 'https://fonts.gstatic.com'; pre2.crossOrigin = '';
      document.head.appendChild(pre2);
      const link = document.createElement('link');
      link.id   = 'tsd-gfonts';
      link.rel  = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=' + families.join('&family=') + '&display=swap';
      document.head.appendChild(link);
    },

    // ================================================================
    // PREMIUM COLOUR FONTS  (real OpenType-SVG fonts, atlas-rendered)
    // ----------------------------------------------------------------
    // camo / paint / mermaid / ... are genuine multi-colour SVG fonts. An HTML
    // <canvas> cannot paint colour-font glyphs, so each font ships as a
    // sprite-sheet atlas (assets/pf-<key>.png) plus metrics (pf-<key>.json),
    // generated by tools/premium-fonts/. Premium text is composed from those
    // sprites onto an offscreen canvas and placed on the design as a baked
    // image, so it "prints exactly as shown" and the colour picker stays locked.
    // The atlas URLs are injected by the Liquid section as window.TSD_PREMIUM_ATLASES.
    // ================================================================
    PREMIUM_FONTS: [
      { key: 'block',      label: 'Blocks'     },
      { key: 'camo',       label: 'Camo'       },
      { key: 'spidey',     label: 'Spider'     },
      { key: 'cozy',       label: 'Cozy'       },
      { key: 'football',   label: 'Football'   },
      { key: 'texture',    label: 'Kids'       },
      { key: 'flower',     label: 'Flower'     },
      { key: 'paint',      label: 'Paint'      },
      { key: 'mermaid',    label: 'Mermaid'    },
      { key: 'reflection', label: 'Reflection' },
    ],

    _pendingPremium: '',
    _atlas: {},                 // key -> { meta, img }
    _atlasPromises: {},         // key -> Promise
    _premiumPreviewToken: 0,

    _premiumDef(key) { return this.PREMIUM_FONTS.find(f => f.key === key) || null; },
    _isPremiumObj(o) { return !!(o && o._premiumKey); },
    _atlasSources()  { return window.TSD_PREMIUM_ATLASES || {}; },

    // Lazily load + cache one font's atlas image. Metrics come from the global
    // window.TSD_PREMIUM_META (shipped as a <script>, so no cross-origin fetch);
    // only the sprite-sheet PNG is loaded here, on demand. Returns a Promise.
    loadAtlas(key) {
      if (this._atlas[key]) return Promise.resolve(this._atlas[key]);
      if (this._atlasPromises[key]) return this._atlasPromises[key];
      const src  = this._atlasSources()[key];
      const meta = (window.TSD_PREMIUM_META || {})[key];
      if (!src || !src.png || !meta) {
        return Promise.reject(new Error('No atlas registered for ' + key));
      }
      const p = new Promise((res, rej) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';   // keep the design canvas exportable
        img.onload  = () => { const a = { meta, img }; this._atlas[key] = a; res(a); };
        img.onerror = () => rej(new Error('Atlas image failed: ' + key));
        img.src = src.png;
      });
      this._atlasPromises[key] = p;
      return p;
    },

    // Compose `text` into an offscreen canvas at the atlas's native resolution.
    // Returns { canvas, emPx } or null if the atlas isn't loaded yet.
    _composePremiumCanvas(key, text) {
      const a = this._atlas[key];
      if (!a) return null;
      const meta = a.meta, img = a.img, G = meta.glyphs, emPx = meta.emPx;
      const spaceAdv = (G[' '] && G[' '].adv) || emPx * 0.3;
      const PAD = Math.ceil(0.14 * emPx);   // room for decorative side overhang
      const VP  = Math.ceil(0.10 * emPx);   // vertical breathing room

      const lineH = meta.lineHeightPx;

      // Lay each line out separately. A '\n' has no glyph in the atlas, so
      // folding it into the pen run below would silently draw it as a space.
      const lines = String(text).replace(/\r\n?/g, '\n').split('\n').map((line) => {
        const items = [];
        let pen = 0;
        for (const ch of line) {
          let g = G[ch] || G[ch.toUpperCase()] || G[ch.toLowerCase()];
          if (!g) { pen += spaceAdv; continue; }
          if (g.space || g.w == null) { pen += (g.adv || spaceAdv); continue; }
          items.push({ g: g, pen: pen });
          pen += g.adv;
        }
        return { items: items, width: pen };
      });

      const maxW = lines.reduce((m, l) => Math.max(m, l.width), 0);
      const W = Math.max(1, Math.ceil(maxW) + 2 * PAD);
      const H = Math.ceil(lineH * lines.length) + 2 * VP;
      const c = document.createElement('canvas');
      c.width = W; c.height = H;
      const x = c.getContext('2d');
      lines.forEach((line, i) => {
        const startX = PAD + (maxW - line.width) / 2;   // centre each line
        const base   = meta.ascentPx + VP + i * lineH;
        for (const it of line.items) {
          const g = it.g;
          x.drawImage(img, g.x, g.y, g.w, g.h,
                      startX + it.pen + g.left, base + g.top, g.w, g.h);
        }
      });
      return { canvas: c, emPx: emPx };
    },

    // Build a fabric.Image of `text` in the premium font, scaled so its em
    // height equals `fontSize`. Async (loads the atlas on first use); calls
    // cb(img) with the image, or cb(null) on failure.
    _makePremiumImage(key, text, fontSize, baseProps, cb) {
      this.loadAtlas(key).then(() => {
        const comp = this._composePremiumCanvas(key, text);
        if (!comp || comp.canvas.width < 2) { cb && cb(null); return; }
        let dataUrl;
        try { dataUrl = comp.canvas.toDataURL('image/png'); }
        catch (e) { cb && cb(null); return; }   // tainted (shouldn't happen)
        const scale = fontSize / comp.emPx;
        fabric.Image.fromURL(dataUrl, (img) => {
          img.set(Object.assign({
            originX: 'center', originY: 'center',
            scaleX: scale, scaleY: scale,
          }, baseProps || {}));
          img._premiumKey      = key;
          img._premiumText     = text;
          img._premiumFontSize = fontSize;
          img._premiumEmPx     = comp.emPx;
          cb && cb(img);
        });
      }).catch(() => { cb && cb(null); });
    },

    // Live preview while typing with a premium font selected.
    _updatePremiumLivePreview(key, text, fontSize) {
      const prev = this._livePreviewObj;
      const pos = prev
        ? { left: prev.left, top: prev.top, angle: prev.angle || 0, originX: 'center', originY: 'center' }
        : { left: this.canvas.width / 2, top: this.canvas.height / 2, originX: 'center', originY: 'center' };
      const token = (this._premiumPreviewToken += 1);
      this._makePremiumImage(key, text, fontSize, pos, (img) => {
        if (token !== this._premiumPreviewToken) return;   // a newer keystroke won
        if (this._livePreviewObj) { this.canvas.remove(this._livePreviewObj); this._livePreviewObj = null; }
        if (!img) { this.canvas.requestRenderAll(); return; }
        img.id = '_live_preview';
        this._livePreviewObj = img;
        this.canvas.add(img);
        this.canvas.setActiveObject(img);
        this.canvas.requestRenderAll();
        this.onSelectionChange();
      });
    },

    // Rescale an existing premium image to a new font size (no re-compose).
    _resizePremiumImage(obj, fontSize) {
      if (!this._isPremiumObj(obj)) return;
      const emPx = obj._premiumEmPx || 220;
      const scale = fontSize / emPx;
      obj.set({ scaleX: scale, scaleY: scale });
      obj._premiumFontSize = fontSize;
      obj.setCoords();
    },

    // Apply / switch the premium font on the active object (from the picker).
    applyPremiumToActive(key) {
      const active = this.canvas.getActiveObject();
      if (!active) return;
      if (!key) {                                   // "None" -> back to plain text
        if (this._isPremiumObj(active)) this._convertPremiumToText(active);
        return;
      }
      const text = this._isPremiumObj(active)
        ? active._premiumText
        : ((active.type === 'text' || active.type === 'i-text') ? active.text : '');
      if (!text) return;
      const fontSize = this._isPremiumObj(active) ? (active._premiumFontSize || 40) : (active.fontSize || 40);
      const pos = { left: active.left, top: active.top, angle: active.angle || 0, originX: 'center', originY: 'center' };
      const oldId = active.id;
      this._makePremiumImage(key, text, fontSize, pos, (img) => {
        if (!img) return;
        img.id = oldId || ('text-' + Date.now());
        if (this._livePreviewObj === active) this._livePreviewObj = img.id === '_live_preview' ? img : null;
        this.canvas.remove(active);
        this.canvas.add(img);
        this.canvas.setActiveObject(img);
        this.canvas.requestRenderAll();
        this.onSelectionChange();
        this._debouncePushHistory && this._debouncePushHistory();
      });
    },

    // Convert a premium image back into an editable plain-text object.
    _convertPremiumToText(obj) {
      const text = obj._premiumText || '';
      const fontSize = obj._premiumFontSize || 40;
      const fill = document.getElementById('tsd-text-colour')?.value || '#191946';
      const t = new fabric.Text(text, {
        left: obj.left, top: obj.top, angle: obj.angle || 0,
        originX: 'center', originY: 'center',
        fontFamily: 'Arial', fontSize: fontSize, fill: fill,
      });
      t.id = obj.id || ('text-' + Date.now());
      this.canvas.remove(obj);
      this.canvas.add(t);
      this.canvas.setActiveObject(t);
      this.canvas.requestRenderAll();
      this.onSelectionChange();
      this._debouncePushHistory && this._debouncePushHistory();
    },

    // Premium images bake their colours into pixels, so they round-trip through
    // toJSON/loadFromJSON with no rebuild. Kept as a safe no-op for callers.
    reapplyPremiumFonts(/* canvas */) {},

    _setColourLock(locked) {
      const input = document.getElementById('tsd-text-colour');
      const field = input ? input.closest('.tsd-colour-field') : null;
      const note  = document.getElementById('tsd-premium-note');
      if (input) input.disabled = locked;
      if (field) field.classList.toggle('tsd-colour-field--locked', locked);
      if (note)  note.hidden = !locked;
    },

    _syncPremiumButtons(key) {
      document.querySelectorAll('.tsd-premium-font').forEach(b => {
        b.classList.toggle('tsd-premium-font--active', (b.dataset.premium || '') === (key || ''));
      });
    },

    buildPremiumFonts() {
      const wrap = document.getElementById('tsd-premium-fonts');
      if (!wrap) return;
      wrap.innerHTML = '';
      const sources = this._atlasSources();

      const none = document.createElement('button');
      none.type = 'button';
      none.className = 'tsd-premium-font tsd-premium-font--active';
      none.dataset.premium = '';
      none.innerHTML = '<span class="tsd-premium-none">Aa</span><span class="tsd-premium-label">None</span>';
      wrap.appendChild(none);

      this.PREMIUM_FONTS.forEach(def => {
        const src = sources[def.key];
        if (!src) return;                       // atlas not registered -> hide
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tsd-premium-font';
        btn.dataset.premium = def.key;
        const img = document.createElement('img');
        img.alt = def.label; img.className = 'tsd-premium-prev';
        img.loading = 'lazy';
        if (src.prev) img.src = src.prev;
        const lbl = document.createElement('span');
        lbl.className = 'tsd-premium-label'; lbl.textContent = def.label;
        btn.appendChild(img); btn.appendChild(lbl);
        wrap.appendChild(btn);
      });

      wrap.querySelectorAll('.tsd-premium-font').forEach(btn => {
        btn.addEventListener('click', () => {
          const key = btn.dataset.premium || '';
          this._pendingPremium = key;
          this._syncPremiumButtons(key);
          this._setColourLock(!!key);
          const active = this.canvas.getActiveObject();
          if (active && active !== this._livePreviewObj &&
              (this._isPremiumObj(active) || active.type === 'text' || active.type === 'i-text')) {
            this.applyPremiumToActive(key);
          } else {
            this._updateLivePreview();
          }
          // warm the atlas so the next keystroke renders instantly
          if (key) this.loadAtlas(key).catch(() => {});
        });
      });
    },

    // ================================================================
    // THEMED DIALOGS & TOASTS (replace native alert/confirm/prompt)
    // ================================================================
    _ensureModalRoot() {
      if (this._modalRoot) return this._modalRoot;
      const root = document.createElement('div');
      root.className = 'tsd-modal-root';
      root.setAttribute('hidden', '');
      root.innerHTML =
        '<div class="tsd-modal-backdrop"></div>' +
        '<div class="tsd-modal" role="dialog" aria-modal="true" aria-labelledby="tsd-modal-title">' +
          '<div class="tsd-modal-icon" aria-hidden="true"></div>' +
          '<h3 class="tsd-modal-title" id="tsd-modal-title"></h3>' +
          '<p class="tsd-modal-msg"></p>' +
          '<div class="tsd-modal-actions"></div>' +
        '</div>';
      document.body.appendChild(root);
      this._modalRoot = root;
      return root;
    },

    // Low-level themed dialog. Resolves with the value of the clicked button
    // (or the cancel value on Esc / backdrop click).
    tsdDialog({ title = '', message = '', icon = 'info', buttons = [] }) {
      return new Promise((resolve) => {
        const root     = this._ensureModalRoot();
        const modal    = root.querySelector('.tsd-modal');
        const backdrop = root.querySelector('.tsd-modal-backdrop');
        const titleEl  = root.querySelector('.tsd-modal-title');
        const msgEl    = root.querySelector('.tsd-modal-msg');
        const iconEl   = root.querySelector('.tsd-modal-icon');
        const actions  = root.querySelector('.tsd-modal-actions');

        titleEl.textContent   = title;
        titleEl.style.display = title ? '' : 'none';
        msgEl.textContent     = message;
        iconEl.className       = 'tsd-modal-icon tsd-modal-icon--' + icon;

        let cancelValue = null;
        actions.innerHTML = '';

        const close = (val) => {
          document.removeEventListener('keydown', onKey);
          modal.classList.remove('tsd-modal--in');
          setTimeout(() => root.setAttribute('hidden', ''), 180);
          resolve(val);
        };
        const onKey = (e) => {
          if (e.key === 'Escape') { e.preventDefault(); close(cancelValue); }
          else if (e.key === 'Enter') {
            const primary = buttons.find(b => b.variant === 'primary');
            if (primary) { e.preventDefault(); close(primary.value); }
          }
        };

        buttons.forEach(b => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'tsd-modal-btn ' +
            (b.variant === 'primary' ? 'tsd-modal-btn--primary' : 'tsd-modal-btn--ghost');
          btn.textContent = b.label;
          btn.addEventListener('click', () => close(b.value));
          actions.appendChild(btn);
          if (b.cancel) cancelValue = b.value;
        });

        backdrop.onclick = () => close(cancelValue);
        document.addEventListener('keydown', onKey);

        root.removeAttribute('hidden');
        requestAnimationFrame(() => modal.classList.add('tsd-modal--in'));
        const focusBtn = actions.querySelector('.tsd-modal-btn--primary') ||
                         actions.querySelector('.tsd-modal-btn');
        if (focusBtn) focusBtn.focus();
      });
    },

    tsdAlert(message, { title = '', icon = 'info', okLabel = 'OK' } = {}) {
      return this.tsdDialog({
        title, message, icon,
        buttons: [{ label: okLabel, value: true, variant: 'primary' }],
      });
    },

    tsdConfirm(message, { title = '', icon = 'question', okLabel = 'Continue', cancelLabel = 'Cancel' } = {}) {
      return this.tsdDialog({
        title, message, icon,
        buttons: [
          { label: cancelLabel, value: false, variant: 'ghost', cancel: true },
          { label: okLabel,     value: true,  variant: 'primary' },
        ],
      });
    },

    // Brief, non-blocking notification (bottom of screen).
    tsdToast(message, { type = 'info', duration = 3400 } = {}) {
      let host = document.getElementById('tsd-toast-host');
      if (!host) {
        host = document.createElement('div');
        host.id = 'tsd-toast-host';
        host.className = 'tsd-toast-host';
        document.body.appendChild(host);
      }
      const t = document.createElement('div');
      t.className = 'tsd-toast tsd-toast--' + type;
      t.setAttribute('role', 'status');
      t.textContent = message;
      host.appendChild(t);
      requestAnimationFrame(() => t.classList.add('tsd-toast--in'));
      setTimeout(() => {
        t.classList.remove('tsd-toast--in');
        setTimeout(() => t.remove(), 300);
      }, duration);
    },

  }; // end TshirtDesigner

  // ================================================================
  // BOOTSTRAP — runs AFTER TshirtDesigner is defined above
  // ================================================================
  function init() {
    if (typeof fabric === 'undefined') {
      setTimeout(init, 100);
      return;
    }

    const canvasEl = document.getElementById('tsd-canvas');
    if (!canvasEl) return; // section not on this page

    // Guard against double-init (e.g. theme editor live-reload)
    if (canvasEl.dataset.tsdReady) return;
    canvasEl.dataset.tsdReady = '1';

    TshirtDesigner.init();
  }

  // Safe init: works whether DOM is still loading or already ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* ================================================================
   MOBILE BOTTOM-SHEET — design tools slide up; the shirt stays put.
   Self-contained; runs after `defer` so the DOM is already parsed.
   ================================================================ */
(function () {
  'use strict';

  var mq       = window.matchMedia('(max-width: 749px)');
  var fab      = document.getElementById('tsd-mobile-edit-btn');
  var panel    = document.getElementById('tsd-left-panel');
  var backdrop = document.getElementById('tsd-sheet-backdrop');
  var closeBtn = document.getElementById('tsd-sheet-close');
  var handle   = document.getElementById('tsd-sheet-handle');
  if (!panel || !fab) return;

  /* Move the trigger + backdrop up to <body> so their fixed
     positioning can't be trapped or hidden by a transformed/clipped/
     stacked ancestor in the theme wrappers. (Both are display:none on
     desktop, so this is harmless there. The panel stays in .tsd-layout
     so the desktop grid layout is untouched.) */
  [fab, backdrop].forEach(function (el) {
    if (el && el.parentNode !== document.body) document.body.appendChild(el);
  });

  var isOpen = false;

  function lockBody(on) {
    document.body.classList.toggle('tsd-sheet-active', on);
    document.body.style.overflow = on ? 'hidden' : '';
  }

  function openSheet() {
    isOpen = true;
    panel.style.transform = '';
    panel.classList.add('tsd-sheet-open');
    if (backdrop) backdrop.classList.add('is-open');
    lockBody(true);
  }

  function closeSheet() {
    isOpen = false;
    panel.style.transform = '';
    panel.classList.remove('tsd-sheet-open');
    if (backdrop) backdrop.classList.remove('is-open');
    lockBody(false);
  }

  fab.addEventListener('click', openSheet);
  if (closeBtn) closeBtn.addEventListener('click', closeSheet);
  if (backdrop) backdrop.addEventListener('click', closeSheet);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen) closeSheet();
  });

  /* ---- Swipe-down on the handle to dismiss ---- */
  var startY = 0, curY = 0, dragging = false;

  function dragStart(e) {
    if (!isOpen) return;
    dragging = true;
    startY = e.touches ? e.touches[0].clientY : e.clientY;
    curY = startY;
    panel.style.transition = 'none';
  }
  function dragMove(e) {
    if (!dragging) return;
    curY = e.touches ? e.touches[0].clientY : e.clientY;
    var dy = Math.max(0, curY - startY);
    panel.style.transform = 'translateY(' + dy + 'px)';
  }
  function dragEnd() {
    if (!dragging) return;
    dragging = false;
    panel.style.transition = '';
    if (curY - startY > 110) {
      closeSheet();
    } else {
      panel.style.transform = '';
    }
    startY = curY = 0;
  }

  if (handle) {
    handle.addEventListener('touchstart', dragStart, { passive: true });
    handle.addEventListener('touchmove', dragMove, { passive: true });
    handle.addEventListener('touchend', dragEnd);
  }

  /* ---- Reset when growing past the mobile breakpoint ---- */
  function onBreakpoint() {
    if (!mq.matches && isOpen) closeSheet();
  }
  if (mq.addEventListener) {
    mq.addEventListener('change', onBreakpoint);
  } else if (mq.addListener) {
    mq.addListener(onBreakpoint);
  }
})();
